
import React, { useState, useEffect } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails,
  Grid, FormControl, InputLabel, MenuItem, Select, TextField,
  Paper, Container, Button, Box, Checkbox, FormControlLabel,
  RadioGroup, Radio, Typography, Tabs, Tab, Alert, CircularProgress,
    Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';

const VestibularForm = () => {
  const [date, setDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
 
      const [saveStatus, setSaveStatus] = useState({ message: '', severity: '' });
      const [openPasswordModal, setOpenPasswordModal] = useState(false);
      const [password, setPassword] = useState('');
      const [downloadFormat, setDownloadFormat] = useState('excel');

  // Accordion state
  const [expandedSection, setExpandedSection] = useState({
    patientDetails: true,
    problemDescription: false,
    healthStatus: false,
    behavioralAssessment: false,
    questionnaire: false,
    objectivetest: false,
    vngTesting: false,
    positionalTests: false
  });

  // State for active tabs
  const [positionalTestType, setPositionalTestType] = useState('dixHallpike');
  const [vempType, setVempType] = useState('cVEMP');
  const [vngType, setVngType] = useState('svv');
  const [headImpulseType, setHeadImpulseType] = useState('vhit');

  // Search state
  const [searchIdentifier, setSearchIdentifier] = useState('');
  const [patientData, setPatientData] = useState(null);
  const [existingAssessmentId, setExistingAssessmentId] = useState(null);

  // Combined form data state
  const [formData, setFormData] = useState({
    identifier: '',
    age: '',
    gender: '',
    ses: '',
    occupation: '',
    familyHistory: '',
    education: '',
    mocaScore: '',
    associatedProblems: [],
    dateOfAssessment: ''
  });

  // Problem description state
  const [problemDescription, setProblemDescription] = useState({});

  // Health status state
  const [healthStatus, setHealthStatus] = useState({});

  // Behavioral assessment state
  const [behavioralAssessment, setBehavioralAssessment] = useState({
    motilityHTest: { response: '', details: '' },
    convergenceInsufficiencyTest: { response: '', details: '' },
    coverUncoverTest: { response: '', details: '' },
    alternateCoverUncoverTest: { response: '', details: '' },
    dynamicVisualAcuity: { response: '', details: '' },
    saccadeFingerToNoseTest: { response: '', details: '' },
    spontaneousNystagmus: { response: '', details: '' },
    headShake: { response: '', details: '' },
    hyperventilation: { response: '', details: '' },
    pressureVariation: { response: '', details: '' },
    orthostatic: { response: '', details: '' },
    boneConduction: { response: '', details: '' },
    nystagmusSuppression: { response: '', details: '' },
    hintsHeadImpulse: { response: '', details: '' },
    hintsNystagmus: { response: '', details: '' },
    hintsSkewDeviation: { response: '', details: '' },
    hintsInterpretation: { response: '', details: '' },
    gazeStabilisation: { response: '', details: '' },
    oscillopsiaScreening: { response: '', details: '' },
    vorSuppression: { response: '', details: '' },
    firmSurfaceRomberg: { response: '', details: '' },
    foamSurfaceRomberg: { response: '', details: '' },
    tandemRomberg: { response: '', details: '' },
    rombergEyesClosed: { response: '', details: '' },
    jendrassikRomberg: { response: '', details: '' },
    modifiedStanceEyesOpen: { response: '', details: '' },
    modifiedStanceEyesClosed: { response: '', details: '' },
    fukudaEyesOpen: { response: '', details: '' },
    fukudaEyesClosed: { response: '', details: '' },
    tandemGait: { response: '', details: '' },
    singleLegStance: { response: '', details: '' },
    mCTSIBFirmEyesOpen: { response: '', details: '' },
    mCTSIBFirmEyesClosed: { response: '', details: '' },
    mCTSIBFoamEyesOpen: { response: '', details: '' },
    mCTSIBFoamEyesClosed: { response: '', details: '' },
    fingerToNose: { response: '', details: '' },
    heelToShin: { response: '', details: '' },
    pastPointing: { response: '', details: '' },
    dysdiadochokinesia: { response: '', details: '' },
    visuallyEnhancedVOR: { response: '', details: '' }
  });

  // Screening scale state
  const [screeningScales, setScreeningScales] = useState({
    vertigoSymptom: { maxScore: 60, patientScore: '' },
    motionSensitivity: { position: '', severity: '' },
    dizzinessHandicap: { functionScore: '', emotionalScore: '', situationalScore: '', totalScore: '' },
    balanceConfidence: { leastConfidence: '', overallScore: '' },
    addsScale: { score: '' },
    mctsib: {
      firmEyesOpen: '',
      firmEyesClosed: '',
      foamEyesOpen: '',
      foamEyesClosed: '',
      totalScore: ''
    }
  });

  // VEMP assessment state
  const [vempData, setVempData] = useState({
    cVemp: {
      right: {
        response: 'Present',
        p13Latency: '',
        p13Amplitude: '',
        n23Latency: '',
        n23Amplitude: '',
        asymmetryRatio: '',
        interpretation: ''
      },
      left: {
        response: 'Present',
        p13Latency: '',
        p13Amplitude: '',
        n23Latency: '',
        n23Amplitude: '',
        asymmetryRatio: '',
        interpretation: ''
      }
    },
    oVemp: {
      right: {
        response: 'Present',
        n1Latency: '',
        n1Amplitude: '',
        p2Latency: '',
        p2Amplitude: '',
        asymmetryRatio: '',
        interpretation: ''
      },
      left: {
        response: 'Present',
        n1Latency: '',
        n1Amplitude: '',
        p2Latency: '',
        p2Amplitude: '',
        asymmetryRatio: '',
        interpretation: ''
      }
    },
    mVemp: {
      right: {
        response: 'Present',
        p13Latency: '',
        p13Amplitude: '',
        n23Latency: '',
        n23Amplitude: '',
        asymmetryRatio: '',
        interpretation: ''
      },
      left: {
        response: 'Present',
        p13Latency: '',
        p13Amplitude: '',
        n23Latency: '',
        n23Amplitude: '',
        asymmetryRatio: '',
        interpretation: ''
      }
    }
  });

  // Head impulse state
  const [headImpulseData, setHeadImpulseData] = useState({
    vhit: {
      rightHorizontal: { response: '', vorGain: '', saccadePresence: '', saccadeType: '', saccadeLatency: '', asymmetry: '', interpretation: '' },
      leftHorizontal: { response: '', vorGain: '', saccadePresence: '', saccadeType: '', saccadeLatency: '', saccadeCatchUp: '', asymmetry: '', interpretation: '' },
      larp: { response: '', vorGain: '', saccadePresence: '', saccadeType: '', saccadeLatency: '', saccadeCatchUp: '', asymmetry: '', interpretation: '' },
      ralp: { response: '', vorGain: '', saccadePresence: '', saccadeType: '', saccadeLatency: '', asymmetry: '', interpretation: '' },
    },
    shimp: {
      rightHorizontal: { response: '', vorGain: '', saccadePresence: '', saccadeType: '', saccadeLatency: '', asymmetry: '', interpretation: '' },
      leftHorizontal: { response: '', vorGain: '', saccadePresence: '', saccadeType: '', saccadeLatency: '', saccadeCatchUp: '', asymmetry: '', interpretation: '' },
      larp: { response: '', vorGain: '', saccadePresence: '', saccadeType: '', saccadeLatency: '', saccadeCatchUp: '', asymmetry: '', interpretation: '' },
      ralp: { response: '', vorGain: '', saccadePresence: '', saccadeType: '', saccadeLatency: '', asymmetry: '', interpretation: '' },
    }
  });

  // VNG data state
  const [vngData, setVngData] = useState({
    svv: {
      right: {
        clockwise: { angle: '', interpretation: '' },
        anticlockwise: { angle: '', interpretation: '' },
        blank: { angle: '', interpretation: '' }
      },
      left: {
        clockwise: { angle: '', interpretation: '' },
        anticlockwise: { angle: '', interpretation: '' },
        blank: { angle: '', interpretation: '' }
      }
    },
    gazeTest: {
      centre: { nystagmus: '', interpretation: '' },
      right: { nystagmus: '', interpretation: '' },
      left: { nystagmus: '', interpretation: '' },
      up: { nystagmus: '', interpretation: '' },
      down: { nystagmus: '', interpretation: '' }
    },
    saccades: {
      horizontal03: { latency: '', accuracy: '', velocity: '', interpretation: '' },
      horizontal06: { latency: '', accuracy: '', velocity: '', interpretation: '' },
      vertical03: { latency: '', accuracy: '', velocity: '', interpretation: '' },
      vertical06: { latency: '', accuracy: '', velocity: '', interpretation: '' }
    },
    optokinetic: {
      leftToRight10: { gainRight: '', gainLeft: '', fastPhaseRight: '', fastPhaseLeft: '', interpretation: '' },
      leftToRight20: { gainRight: '', gainLeft: '', fastPhaseRight: '', fastPhaseLeft: '', interpretation: '' },
      rightToLeft10: { gainRight: '', gainLeft: '', fastPhaseRight: '', fastPhaseLeft: '', interpretation: '' },
      rightToLeft20: { gainRight: '', gainLeft: '', fastPhaseRight: '', fastPhaseLeft: '', interpretation: '' },
      topToBottom10: { gainRight: '', gainLeft: '', fastPhaseRight: '', fastPhaseLeft: '', interpretation: '' },
      topToBottom20: { gainRight: '', gainLeft: '', fastPhaseRight: '', fastPhaseLeft: '', interpretation: '' },
      bottomToTop10: { gainRight: '', gainLeft: '', fastPhaseRight: '', fastPhaseLeft: '', interpretation: '' },
      bottomToTop20: { gainRight: '', gainLeft: '', fastPhaseRight: '', fastPhaseLeft: '', interpretation: '' }
    },
    smoothPursuit: {
      horizontal02: { rightwardRight: '', rightwardLeft: '', leftwardRight: '', leftwardLeft: '', interpretation: '' },
      horizontal04: { rightwardRight: '', rightwardLeft: '', leftwardRight: '', leftwardLeft: '', interpretation: '' },
      vertical02: { upwardRight: '', upwardLeft: '', downwardRight: '', downwardLeft: '', interpretation: '' },
      vertical06: { upwardRight: '', upwardLeft: '', downwardRight: '', downwardLeft: '', interpretation: '' }
    }
  });

  // Positional test data
  const [positionalData, setPositionalData] = useState({
    dixHallpike: {
      right: {
        nystagmus: { present: false, type: '' },
        nystagmusDirection: '',
        changeDirection: false,
        latency: '',
        vertigo: { intensity: '', duration: '' },
        recoveryDuration: '',
        interpretation: ''
      },
      left: {
        nystagmus: { present: false, type: '' },
        nystagmusDirection: '',
        changeDirection: false,
        latency: '',
        vertigo: { intensity: '', duration: '' },
        recoveryDuration: '',
        interpretation: ''
      }
    },
    supineRoll: {
      right: {
        nystagmus: { present: false, type: '' },
        nystagmusDirection: '',
        changeDirection: false,
        latency: '',
        vertigo: { intensity: '', duration: '' },
        recoveryDuration: '',
        interpretation: ''
      },
      left: {
        nystagmus: { present: false, type: '' },
        nystagmusDirection: '',
        changeDirection: false,
        latency: '',
        vertigo: { intensity: '', duration: '' },
        recoveryDuration: '',
        interpretation: ''
      }
    },
    deepHeadHanging: {
      right: {
        nystagmus: { present: false, type: '' },
        nystagmusDirection: '',
        changeDirection: false,
        latency: '',
        vertigo: { intensity: '', duration: '' },
        recoveryDuration: '',
        interpretation: ''
      },
      left: {
        nystagmus: { present: false, type: '' },
        nystagmusDirection: '',
        changeDirection: false,
        latency: '',
        vertigo: { intensity: '', duration: '' },
        recoveryDuration: '',
        interpretation: ''
      }
    }
  });

  // ---------- SEARCH FUNCTIONALITY ----------
  const handleSearch = async () => {
    if (!searchIdentifier.trim()) {
      setMessage({ type: 'error', text: 'Please enter an OP/IP number' });
      return;
    }

    setSearchLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/vestibular/search/${searchIdentifier.trim()}`);
      
      if (response.ok) {
        const data = await response.json();
        setPatientData(data.patient);
        
        if (data.assessment) {
          // Load existing assessment data
          setExistingAssessmentId(data.assessment.id);
          setFormData(prev => ({ ...prev, ...data.assessment.patient_details }));
          setProblemDescription(data.assessment.problem_description || {});
          setHealthStatus(data.assessment.health_status || {});
          setBehavioralAssessment(data.assessment.behavioral_assessment || {});
          setScreeningScales(data.assessment.screening_scales || {});
          setVempData(data.assessment.vemp_data || {});
          setHeadImpulseData(data.assessment.head_impulse_data || {});
          setVngData(data.assessment.vng_data || {});
          setPositionalData(data.assessment.positional_tests || {});
          setDate(data.assessment.date_of_assessment || '');
          
          setMessage({ type: 'success', text: 'Existing assessment data loaded successfully!' });
        } else {
          setExistingAssessmentId(null);
          // Reset form for new patient
          setFormData(prev => ({ 
            ...prev, 
            identifier: searchIdentifier.trim(),
            age: '',
            gender: '',
            ses: '',
            occupation: '',
            familyHistory: '',
            education: '',
            mocaScore: '',
            associatedProblems: []
          }));
          setProblemDescription({});
          setHealthStatus({});
          setBehavioralAssessment({
            motilityHTest: { response: '', details: '' },
            convergenceInsufficiencyTest: { response: '', details: '' },
            coverUncoverTest: { response: '', details: '' },
            alternateCoverUncoverTest: { response: '', details: '' },
            dynamicVisualAcuity: { response: '', details: '' },
            saccadeFingerToNoseTest: { response: '', details: '' },
            spontaneousNystagmus: { response: '', details: '' },
            headShake: { response: '', details: '' },
            hyperventilation: { response: '', details: '' },
            pressureVariation: { response: '', details: '' },
            orthostatic: { response: '', details: '' },
            boneConduction: { response: '', details: '' },
            nystagmusSuppression: { response: '', details: '' },
            hintsHeadImpulse: { response: '', details: '' },
            hintsNystagmus: { response: '', details: '' },
            hintsSkewDeviation: { response: '', details: '' },
            hintsInterpretation: { response: '', details: '' },
            gazeStabilisation: { response: '', details: '' },
            oscillopsiaScreening: { response: '', details: '' },
            vorSuppression: { response: '', details: '' },
            firmSurfaceRomberg: { response: '', details: '' },
            foamSurfaceRomberg: { response: '', details: '' },
            tandemRomberg: { response: '', details: '' },
            rombergEyesClosed: { response: '', details: '' },
            jendrassikRomberg: { response: '', details: '' },
            modifiedStanceEyesOpen: { response: '', details: '' },
            modifiedStanceEyesClosed: { response: '', details: '' },
            fukudaEyesOpen: { response: '', details: '' },
            fukudaEyesClosed: { response: '', details: '' },
            tandemGait: { response: '', details: '' },
            singleLegStance: { response: '', details: '' },
            mCTSIBFirmEyesOpen: { response: '', details: '' },
            mCTSIBFirmEyesClosed: { response: '', details: '' },
            mCTSIBFoamEyesOpen: { response: '', details: '' },
            mCTSIBFoamEyesClosed: { response: '', details: '' },
            fingerToNose: { response: '', details: '' },
            heelToShin: { response: '', details: '' },
            pastPointing: { response: '', details: '' },
            dysdiadochokinesia: { response: '', details: '' },
            visuallyEnhancedVOR: { response: '', details: '' }
          });
          setScreeningScales({
            vertigoSymptom: { maxScore: 60, patientScore: '' },
            motionSensitivity: { position: '', severity: '' },
            dizzinessHandicap: { functionScore: '', emotionalScore: '', situationalScore: '', totalScore: '' },
            balanceConfidence: { leastConfidence: '', overallScore: '' },
            addsScale: { score: '' },
            mctsib: {
              firmEyesOpen: '',
              firmEyesClosed: '',
              foamEyesOpen: '',
              foamEyesClosed: '',
              totalScore: ''
            }
          });
          setVempData({
            cVemp: {
              right: { response: 'Present', p13Latency: '', p13Amplitude: '', n23Latency: '', n23Amplitude: '', asymmetryRatio: '', interpretation: '' },
              left: { response: 'Present', p13Latency: '', p13Amplitude: '', n23Latency: '', n23Amplitude: '', asymmetryRatio: '', interpretation: '' }
            },
            oVemp: {
              right: { response: 'Present', n1Latency: '', n1Amplitude: '', p2Latency: '', p2Amplitude: '', asymmetryRatio: '', interpretation: '' },
              left: { response: 'Present', n1Latency: '', n1Amplitude: '', p2Latency: '', p2Amplitude: '', asymmetryRatio: '', interpretation: '' }
            },
            mVemp: {
              right: { response: 'Present', p13Latency: '', p13Amplitude: '', n23Latency: '', n23Amplitude: '', asymmetryRatio: '', interpretation: '' },
              left: { response: 'Present', p13Latency: '', p13Amplitude: '', n23Latency: '', n23Amplitude: '', asymmetryRatio: '', interpretation: '' }
            }
          });
          setHeadImpulseData({
            vhit: {
              rightHorizontal: { response: '', vorGain: '', saccadePresence: '', saccadeType: '', saccadeLatency: '', asymmetry: '', interpretation: '' },
              leftHorizontal: { response: '', vorGain: '', saccadePresence: '', saccadeType: '', saccadeLatency: '', saccadeCatchUp: '', asymmetry: '', interpretation: '' },
              larp: { response: '', vorGain: '', saccadePresence: '', saccadeType: '', saccadeLatency: '', saccadeCatchUp: '', asymmetry: '', interpretation: '' },
              ralp: { response: '', vorGain: '', saccadePresence: '', saccadeType: '', saccadeLatency: '', asymmetry: '', interpretation: '' },
            },
            shimp: {
              rightHorizontal: { response: '', vorGain: '', saccadePresence: '', saccadeType: '', saccadeLatency: '', asymmetry: '', interpretation: '' },
              leftHorizontal: { response: '', vorGain: '', saccadePresence: '', saccadeType: '', saccadeLatency: '', saccadeCatchUp: '', asymmetry: '', interpretation: '' },
              larp: { response: '', vorGain: '', saccadePresence: '', saccadeType: '', saccadeLatency: '', saccadeCatchUp: '', asymmetry: '', interpretation: '' },
              ralp: { response: '', vorGain: '', saccadePresence: '', saccadeType: '', saccadeLatency: '', asymmetry: '', interpretation: '' },
            }
          });
          setVngData({
            svv: {
              right: { clockwise: { angle: '', interpretation: '' }, anticlockwise: { angle: '', interpretation: '' }, blank: { angle: '', interpretation: '' } },
              left: { clockwise: { angle: '', interpretation: '' }, anticlockwise: { angle: '', interpretation: '' }, blank: { angle: '', interpretation: '' } }
            },
            gazeTest: {
              centre: { nystagmus: '', interpretation: '' }, right: { nystagmus: '', interpretation: '' }, left: { nystagmus: '', interpretation: '' }, up: { nystagmus: '', interpretation: '' }, down: { nystagmus: '', interpretation: '' }
            },
            saccades: {
              horizontal03: { latency: '', accuracy: '', velocity: '', interpretation: '' }, horizontal06: { latency: '', accuracy: '', velocity: '', interpretation: '' }, vertical03: { latency: '', accuracy: '', velocity: '', interpretation: '' }, vertical06: { latency: '', accuracy: '', velocity: '', interpretation: '' }
            },
            optokinetic: {
              leftToRight10: { gainRight: '', gainLeft: '', fastPhaseRight: '', fastPhaseLeft: '', interpretation: '' }, leftToRight20: { gainRight: '', gainLeft: '', fastPhaseRight: '', fastPhaseLeft: '', interpretation: '' }, rightToLeft10: { gainRight: '', gainLeft: '', fastPhaseRight: '', fastPhaseLeft: '', interpretation: '' }, rightToLeft20: { gainRight: '', gainLeft: '', fastPhaseRight: '', fastPhaseLeft: '', interpretation: '' }, topToBottom10: { gainRight: '', gainLeft: '', fastPhaseRight: '', fastPhaseLeft: '', interpretation: '' }, topToBottom20: { gainRight: '', gainLeft: '', fastPhaseRight: '', fastPhaseLeft: '', interpretation: '' }, bottomToTop10: { gainRight: '', gainLeft: '', fastPhaseRight: '', fastPhaseLeft: '', interpretation: '' }, bottomToTop20: { gainRight: '', gainLeft: '', fastPhaseRight: '', fastPhaseLeft: '', interpretation: '' }
            },
            smoothPursuit: {
              horizontal02: { rightwardRight: '', rightwardLeft: '', leftwardRight: '', leftwardLeft: '', interpretation: '' }, horizontal04: { rightwardRight: '', rightwardLeft: '', leftwardRight: '', leftwardLeft: '', interpretation: '' }, vertical02: { upwardRight: '', upwardLeft: '', downwardRight: '', downwardLeft: '', interpretation: '' }, vertical06: { upwardRight: '', upwardLeft: '', downwardRight: '', downwardLeft: '', interpretation: '' }
            }
          });
          setPositionalData({
            dixHallpike: {
              right: { nystagmus: { present: false, type: '' }, nystagmusDirection: '', changeDirection: false, latency: '', vertigo: { intensity: '', duration: '' }, recoveryDuration: '', interpretation: '' },
              left: { nystagmus: { present: false, type: '' }, nystagmusDirection: '', changeDirection: false, latency: '', vertigo: { intensity: '', duration: '' }, recoveryDuration: '', interpretation: '' }
            },
            supineRoll: {
              right: { nystagmus: { present: false, type: '' }, nystagmusDirection: '', changeDirection: false, latency: '', vertigo: { intensity: '', duration: '' }, recoveryDuration: '', interpretation: '' },
              left: { nystagmus: { present: false, type: '' }, nystagmusDirection: '', changeDirection: false, latency: '', vertigo: { intensity: '', duration: '' }, recoveryDuration: '', interpretation: '' }
            },
            deepHeadHanging: {
              right: { nystagmus: { present: false, type: '' }, nystagmusDirection: '', changeDirection: false, latency: '', vertigo: { intensity: '', duration: '' }, recoveryDuration: '', interpretation: '' },
              left: { nystagmus: { present: false, type: '' }, nystagmusDirection: '', changeDirection: false, latency: '', vertigo: { intensity: '', duration: '' }, recoveryDuration: '', interpretation: '' }
            }
          });
          setDate('');
          setMessage({ type: 'info', text: 'New patient - form ready for assessment' });
        }
        
        // Set the identifier in form data
        setFormData(prev => ({ ...prev, identifier: searchIdentifier.trim() }));
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.error || 'Patient not found' });
        setPatientData(null);
        setExistingAssessmentId(null);
      }
    } catch (error) {
      console.error('Error searching for patient:', error);
      setMessage({ type: 'error', text: 'Error searching for patient. Please check your connection.' });
      setPatientData(null);
      setExistingAssessmentId(null);
    } finally {
      setSearchLoading(false);
    }
  };

  // ---------- SAVE INDIVIDUAL SECTIONS ----------
  const saveSection = async (sectionName, sectionData) => {
    if (!formData.identifier) {
      setMessage({ type: 'error', text: 'Please search for a patient first' });
      return;
    }

    setSaveLoading(true);
    try {
      const payload = {
        identifier: formData.identifier,
        dateOfAssessment: date || new Date().toISOString().split('T')[0],
        [sectionName]: sectionData
      };

      if (existingAssessmentId) {
        payload.assessmentId = existingAssessmentId;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/vestibular/save-section`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.assessmentId && !existingAssessmentId) {
          setExistingAssessmentId(result.assessmentId);
        }
        setMessage({ type: 'success', text: `${sectionName.replace(/_/g, ' ')} saved successfully!` });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: `Error saving ${sectionName}: ${error.error}` });
      }
    } catch (error) {
      console.error(`Error saving ${sectionName}:`, error);
      setMessage({ type: 'error', text: `Error saving ${sectionName}` });
    } finally {
      setSaveLoading(false);
    }
  };

  // Save functions for each section
  const savePatientDetails = () => saveSection('patient_details', formData);
  const saveProblemDescription = () => saveSection('problem_description', problemDescription);
  const saveHealthStatus = () => saveSection('health_status', healthStatus);
  const saveBehavioralAssessment = () => saveSection('behavioral_assessment', behavioralAssessment);
  const saveQuestionnaire = () => saveSection('screening_scales', screeningScales);
  const saveVempAssessment = () => saveSection('vemp_data', vempData);
  const saveHeadImpulse = () => saveSection('head_impulse_data', headImpulseData);
  const saveVngTesting = () => saveSection('vng_data', vngData);
  const savePositionalTests = () => saveSection('positional_tests', positionalData);

  // Individual VNG save functions
  const saveSvv = () => saveSection('vng_data', vngData);
  const saveGaze = () => saveSection('vng_data', vngData);
  const saveSaccades = () => saveSection('vng_data', vngData);
  const saveOpto = () => saveSection('vng_data', vngData);
  const saveSmoothPursuit = () => saveSection('vng_data', vngData);

  // Individual Positional Tests save functions
  const saveDixHallpike = () => saveSection('positional_tests', positionalData);
  const saveSupineRoll = () => saveSection('positional_tests', positionalData);

  // ---------- SUBMIT COMPLETE ASSESSMENT ----------
  const handleSubmit = async () => {
    if (!formData.identifier) {
      setMessage({ type: 'error', text: 'Please search for a patient first' });
      return;
    }

    setLoading(true);
    try {
      const completeData = {
        identifier: formData.identifier,
        dateOfAssessment: date || new Date().toISOString().split('T')[0],
        patient_details: formData,
        problem_description: problemDescription,
        health_status: healthStatus,
        behavioral_assessment: behavioralAssessment,
        screening_scales: screeningScales,
        vemp_data: vempData,
        head_impulse_data: headImpulseData,
        vng_data: vngData,
        positional_tests: positionalData
      };

      if (existingAssessmentId) {
        completeData.assessmentId = existingAssessmentId;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/vestibular/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(completeData),
      });

      if (response.ok) {
        const result = await response.json();
        setExistingAssessmentId(result.assessmentId);
        setMessage({ type: 'success', text: 'Complete assessment submitted successfully!' });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: `Error submitting assessment: ${error.error}` });
      }
    } catch (error) {
      console.error('Error submitting assessment:', error);
      setMessage({ type: 'error', text: 'Error submitting assessment' });
    } finally {
      setLoading(false);
    }
  };

  // ---------- ALL EXISTING HANDLER FUNCTIONS ----------
  const handleFormDataChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleMultiSelectChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({ ...prev, associatedProblems: value }));
  };

  const handleProblemDescriptionChange = (category, subcategory, value) => {
    setProblemDescription(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [subcategory]: {
          response: value,
          details: value === 'yes' ? (prev[category]?.[subcategory]?.details || '') : ''
        }
      }
    }));
  };

  const handleProblemDetailsChange = (category, subcategory, value) => {
    setProblemDescription(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [subcategory]: {
          ...prev[category]?.[subcategory],
          details: value
        }
      }
    }));
  };

  const handleHealthStatusChange = (category, option, value) => {
    setHealthStatus(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [option]: {
          response: value,
          details: value === 'yes' ? (prev[category]?.[option]?.details || '') : ''
        }
      }
    }));
  };

  const handleHealthDetailsChange = (category, option, value) => {
    setHealthStatus(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [option]: {
          ...prev[category]?.[option],
          details: value
        }
      }
    }));
  };

  const handleBehavioralResponseChange = (field, value) => {
    setBehavioralAssessment(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        response: value,
        details: value === 'Yes' ? (prev[field]?.details || '') : ''
      }
    }));
  };

  const handleBehavioralDetailsChange = (field, value) => {
    setBehavioralAssessment(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        details: value
      }
    }));
  };

  const handleScreeningScaleChange = (scale, field, value) => {
    setScreeningScales(prev => ({
      ...prev,
      [scale]: {
        ...prev[scale],
        [field]: value
      }
    }));
  };

  const handleVempTypeChange = (event, newValue) => {
    setVempType(newValue);
  };

  const handleVempResponseChange = (type, side, value) => {
    setVempData(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [side]: {
          ...prev[type][side],
          response: value,
          ...(value === 'Absent' ? {
            p13Latency: '', p13Amplitude: '', n23Latency: '', n23Amplitude: '',
            n1Latency: '', n1Amplitude: '', p2Latency: '', p2Amplitude: '', asymmetryRatio: ''
          } : {})
        }
      }
    }));
  };

  const handleVempFieldChange = (type, side, field, value) => {
    setVempData(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [side]: {
          ...prev[type][side],
          [field]: value
        }
      }
    }));
  };

  const handleHeadImpulseTypeChange = (event, newValue) => {
    setHeadImpulseType(newValue);
  };

  const handleHeadImpulseChange = (section, field, value) => {
    setHeadImpulseData(prev => {
      const updatedData = {
        ...prev,
        [headImpulseType]: {
          ...prev[headImpulseType],
          [section]: {
            ...prev[headImpulseType][section],
            [field]: value
          }
        }
      };

      if (field === 'response' && value === 'Absent') {
        const fieldsToClear = ['vorGain', 'saccadePresence', 'saccadeType', 'saccadeLatency', 'asymmetry', 'saccadeCatchUp'];
        fieldsToClear.forEach(f => {
          updatedData[headImpulseType][section][f] = '';
        });
      }

      return updatedData;
    });
  };

  const handleVngChange = (testType, subTest, field, value) => {
    setVngData(prev => ({
      ...prev,
      [testType]: {
        ...prev[testType],
        [subTest]: {
          ...prev[testType][subTest],
          [field]: value
        }
      }
    }));
  };

  const handlePositionalChange = (testType, side, field, subField, value) => {
    setPositionalData(prev => {
      const newData = { ...prev };
      if (subField) {
        newData[testType][side][field][subField] = value;
      } else {
        newData[testType][side][field] = value;
      }
      return newData;
    });
  };

  const handleAccordionChange = (section) => (event, isExpanded) => {
    setExpandedSection(prev => ({ ...prev, [section]: isExpanded }));
  };

  // Problem categories
  const problemCategories = [
    {
      name: 'Presenting complaints',
      subcategories: [
        'Vertigo', 'Dysequillibrium/Gait instability',
        'Presyncope', 'Light Headedness'
      ]
    },
    {
      name: 'Triggering Factors',
      subcategories: [
        'Altered Head Position', 'Rapid Ascents', 'Neck Extension',
        'Stress', 'Salt Diet', 'Headaches',
        'Change in ear pressure', 'Loud Noise'
      ]
    },
    {
      name: 'Duration of each episode',
      subcategories: [
        'minutes', 'hours', 'days'
      ]
    },
    {
      name: 'Symptoms during giddiness episodes',
      subcategories: [
        'Vomiting', 'Nausea', 'Blurring', 'Fainting',
        'Sweating', 'Breathing Difficulty', 'Weakness',
        'Confusion', 'Headache', 'Spinning or Whirling',
        'Loss of Balance'
      ]
    },
    {
      name: 'Duration of balance Problem',
      subcategories: [
        'Days', 'Weeks', 'Months', 'Years'
      ]
    }
  ];

  // Health status categories
  const healthStatusCategories = [
    {
      name: 'Ophthalmological Problems',
      options: [
        'Occulomotor muscle paresis/paralysis', 'Nystagmus',
        'Blurred Vision', 'Visual Disturbances during Motion',
        'Sensitivity to Light', 'Difficulty with Depth Perception',
        'Visual Vertigo', 'Reduced Visual Acuity', 'Gaze Instability'
      ]
    },
    {
      name: 'Vascular Problems',
      options: [
        'Hypotension', 'Hypertension', 'DM I and II',
        'Hypertension + DM', 'Vertebrobasilar Insufficiency',
        "Meniere's Disease", 'Orthostatic Hypotension',
        'Atherosclerosis', 'Stroke'
      ]
    },
    {
      name: 'Musculoskeletal Problems',
      options: [
        'Muscle Tension and Fatigue', 'Postural Changes',
        'Decreased Physical Activity', 'Gait Abnormalities',
        'Joint Pain', 'Reduced Proprioception',
        'Compensatory Movements', 'Secondary Headaches',
        'Spinal cord disorders and limb weakness or paralysis'
      ]
    },
    {
      name: 'Neurological Problems',
      options: [
        'Vertigo', 'Imbalance and Unsteadiness',
        'Vertical Nystagmus', 'Ataxia',
        'Headaches and Migraines', 'Cognitive Impairment',
        'Brainstem, thalamus or cerebellar lesions',
        'Tumors or Degenerative condition', 'Tinnitus',
        'Dysarthria, Dysphagia, Dyskinesia'
      ]
    },
    {
      name: 'Psychiatric Problems',
      options: [
        'Anxiety', 'Depression', 'Panic Attacks',
        'Avoidance behaviour', 'Cognitive deficits',
        'Poor quality of life', 'Sleep disturbances',
        'Stress'
      ]
    },
    {
      name: 'Otological Problems',
      options: [
        'Otitis media and conductive hearing loss',
        'Tinnitus', 'Aural Fullness',
        "BPPV, Vestibular Neuritis, Meniere's Disease, Tumor",
        'Hearing loss', 'ET dysfunction'
      ]
    },
    {
      name: 'Trauma History',
      options: [
        'Head injury', 'Whiplash Injury', 'Concussion',
        'Temporal bone fractures', 'Barotrauma',
        'Penetrating head injuries'
      ]
    },
    {
      name: 'Medications',
      options: [
        'Antibiotics: Aminoglycoside antibiotics (e.g., gentamicin, streptomycin)',
        'Diuretics: Loop diuretics (e.g., furosemide)',
        'Antiepileptic Drugs: Phenytoin, carbamazepine',
        'Antidepressants: Tricyclic antidepressants (e.g., amitriptyline)',
        'Antipsychotics: haloperidol',
        'Benzodiazepines (e.g., diazepam)',
        'Opioid Analgesics: oxycodone, morphine',
        'Motion Sickness Medications: Antihistamines (e.g., dimenhydrinate, meclizine)'
      ]
    }
  ];

  // Behavioral assessment categories
  const behavioralCategories = [
    {
      name: 'General Testing',
      tests: [
        { key: 'motilityHTest', label: "Motility 'H' test" },
        { key: 'convergenceInsufficiencyTest', label: "Convergence Insufficiency Test" },
        { key: 'coverUncoverTest', label: "Cover-Uncover Test" },
        { key: 'alternateCoverUncoverTest', label: "Alternate Cover-Uncover Test" },
        { key: 'dynamicVisualAcuity', label: "Dynamic Visual Acuity" },
        { key: 'saccadeFingerToNoseTest', label: "Saccade - Finger to Nose Test" },
        { key: 'spontaneousNystagmus', label: "Spontaneous Nystagmus" },
      ]
    },
    {
      name: 'Induced Nystagmus',
      tests: [
        { key: 'headShake', label: "Head Shake Nystagmus" },
        { key: 'hyperventilation', label: "Hyperventilation Nystagmus" },
        { key: 'pressureVariation', label: "Pressure Variation Nystagmus" },
        { key: 'orthostatic', label: "Orthostatic Nystagmus" },
        { key: 'boneConduction', label: "Bone Conduction" },
      ]
    },
    {
      name: 'Nystagmus Suppression',
      tests: [
        { key: 'nystagmusSuppression', label: "Nystagmus Suppression" },
      ]
    },
    {
      name: 'VOR Testing',
      tests: [
        { key: 'hintsHeadImpulse', label: "HINTS Head Impulse" },
        { key: 'hintsNystagmus', label: "HINTS Nystagmus" },
        { key: 'hintsSkewDeviation', label: "HINTS Skew Deviation" },
        { key: 'hintsInterpretation', label: "HINTS Interpretation" },
        { key: 'gazeStabilisation', label: "Gaze Stabilisation" },
        { key: 'oscillopsiaScreening', label: "Oscillopsia Screening" },
        { key: 'vorSuppression', label: "VOR Suppression" },
      ]
    },
    {
      name: 'Static Postural Tests',
      tests: [
        { key: 'firmSurfaceRomberg', label: "Firm Surface Romberg Test" },
        { key: 'foamSurfaceRomberg', label: "Foam Surface Romberg Test" },
        { key: 'tandemRomberg', label: "Tandem Romberg Test" },
        { key: 'rombergEyesClosed', label: "Romberg Test (Eyes Closed)" },
        { key: 'jendrassikRomberg', label: "Jendrassik-Romberg" },
        { key: 'modifiedStanceEyesOpen', label: "Modified Stance Romberg (Eyes Open)" },
        { key: 'modifiedStanceEyesClosed', label: "Modified Stance Romberg (Eyes Closed)" },
      ]
    },
    {
      name: 'Dynamic Postural Tests',
      tests: [
        { key: 'fukudaEyesOpen', label: "Fukuda Stepping Test (Eyes Open)" },
        { key: 'fukudaEyesClosed', label: "Fukuda Stepping Test (Eyes Closed)" },
        { key: 'tandemGait', label: "Tandem Gait (Heel to toe) Test" },
        { key: 'singleLegStance', label: "Single Leg Stance (Dominant Leg)" },
      ]
    },
    {
      name: 'Sensory Integration Tests',
      tests: [
        { key: 'mCTSIBFirmEyesOpen', label: "mCTSIB Firm Surface, Eyes Open" },
        { key: 'mCTSIBFirmEyesClosed', label: "mCTSIB Firm Surface, Eyes Closed" },
        { key: 'mCTSIBFoamEyesOpen', label: "mCTSIB Foam Surface, Eyes Open" },
        { key: 'mCTSIBFoamEyesClosed', label: "mCTSIB Foam Surface, Eyes Closed" },
      ]
    },
    {
      name: 'Cerebellar Tests',
      tests: [
        { key: 'fingerToNose', label: "Finger-to-Nose Test" },
        { key: 'heelToShin', label: "Heel-to-Shin Test" },
        { key: 'pastPointing', label: "Past Pointing Test" },
        { key: 'dysdiadochokinesia', label: "Dysdiadochokinesia" },
        { key: 'visuallyEnhancedVOR', label: "Visually Enhanced VOR" },
      ]
    }
  ];

  // Associated problems options
  const associatedProblemsOptions = [
    'Aural Fullness', 'Facial Weakness', 'Pain', 'Otorrhea', 'Headache',
    'Neurological Abnormalities', 'Hearing Loss', 'Tinnitus', 'Phonophobia',
    'Photophobia', 'Motion Sickness'
  ];

  const positionOptions = [
    'Lying down',
    'Sitting up',
    'Standing',
    'Turning head left',
    'Turning head right',
    'Looking up',
    'Looking down',
    'Rolling over in bed',
    'Bending forward'
  ];

  // Calculate ADDS interpretation
  const getAddsInterpretation = (score) => {
    if (score === '') return '';
    const numScore = parseInt(score, 10);

    if (isNaN(numScore)) return 'Invalid score';
    if (numScore === 0) return 'Cervicogenic Dizziness';
    if (numScore >= 1 && numScore <= 4) return 'Unilateral Vestibular Hypofunction';
    if (numScore >= 5 && numScore <= 19) return 'Positional Vertigo (BPPV)';
    return 'Centrally Mediated Problem';
  };


  
 const handleDownloadClick = () => {
    setOpenPasswordModal(true);
  };

const handleDownloadConfirm = async () => {
  const correctKey = "Varun@6361";

  // Validate key entered in modal
  if (password !== correctKey) {
    alert("Incorrect key! Download canceled.");
    setPassword('');
    setOpenPasswordModal(false);
    return;
  }

  setOpenPasswordModal(false);
  setLoading(true);
  setSaveStatus({
    message: `Requesting Vestibular Assessment ${downloadFormat}...`,
    severity: "info",
  });

  try {
    const resp = await fetch(
      `${process.env.REACT_APP_API_URL}/api/reports/download-form`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          table: "vestibular_assessments",
          formName: "Vestibular_Assessment",
          format: downloadFormat,
          key: password   // 🔥 send KEY instead of password
        }),
      }
    );

    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      setSaveStatus({
        message: `Failed: ${body.message || resp.statusText}`,
        severity: "error",
      });
      return;
    }

    const blob = await resp.blob();
    const disposition = resp.headers.get("content-disposition") || "";

    let filename = `Vestibular_Assessment_${Date.now()}.${
      downloadFormat === "excel" ? "xlsx" : "pdf"
    }`;

    const match = /filename="?([^"]+)"?/.exec(disposition);
    if (match && match[1]) filename = match[1];

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    setSaveStatus({
      message: `Download started: ${filename}`,
      severity: "success",
    });

  } catch (err) {
    console.error("Download error:", err);
    setSaveStatus({
      message: `Error downloading file: ${err.message}`,
      severity: "error",
    });
  } finally {
    setLoading(false);
    setPassword('');
  }
};


  const handlePasswordModalClose = () => {
    setOpenPasswordModal(false);
    setPassword('');
  };
  // ---------- RENDER FUNCTION ----------
  return (
    <Container maxWidth="lg" sx={{ py: 4, fontFamily: 'Roboto, sans-serif' }}>
      <Paper elevation={4} sx={{ p: 3, borderRadius: 3, backgroundColor: '#f5f9ff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
          <Typography variant="h4" gutterBottom fontWeight="bold" color="secondary" align="center">
            Vestibular Assessment Form
          </Typography>
        </Box>

        {/* Message Display */}
        {message.text && (
          <Alert severity={message.type} sx={{ mb: 2 }}>
            {message.text}
          </Alert>
        )}

        {/* Search Bar */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="Search by OP/IP Number"
              variant="outlined"
              value={searchIdentifier}
              onChange={(e) => setSearchIdentifier(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <SearchIcon color="action" sx={{ mr: 1 }} />
                ),
                style: { borderRadius: 10, backgroundColor: '#fff' }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleSearch}
              disabled={searchLoading}
              sx={{
                height: '56px',
                borderRadius: 10,
                fontWeight: 'bold',
                fontSize: '1rem',
                boxShadow: 2
              }}
            >
              {searchLoading ? <CircularProgress size={24} /> : 'Search Patient'}
            </Button>
          </Grid>
        </Grid>
      
        {/* Patient Info Display */}
        {patientData && (
          <Paper elevation={1} sx={{ p: 2, mb: 4, bgcolor: '#e3f2fd' }}>
             <Box sx={{ position: 'absolute', top: 350, right: 140 }}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<DownloadIcon />} 
                onClick={handleDownloadClick}
                disabled={loading}
                sx={{ 
                  borderRadius: 2,
                  boxShadow: 2,
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.3s ease'
                  }
                }}
              >
                Download 
              </Button>
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 2 }}>
              Patient Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography><strong>Name:</strong> {patientData.name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography><strong>Age:</strong> {patientData.age || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography><strong>IP Number:</strong> {patientData.ip_number || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography><strong>OP Number:</strong> {patientData.op_number || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography><strong>Phone:</strong> {patientData.phone || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography><strong>Address:</strong> {patientData.address || 'N/A'}</Typography>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Date Field */}
        {patientData && (
          <Box mb={4} border={1} borderColor="divider" p={2} borderRadius={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Date of Assessment"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  {existingAssessmentId ? 'Editing existing assessment' : 'Creating new assessment'}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Only show form sections after patient is searched */}
        {patientData && (
          <>
            {/* Patient Details Accordion */}
            <Accordion
              expanded={expandedSection.patientDetails}
              onChange={handleAccordionChange('patientDetails')}
              sx={{ mb: 3 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>

          
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
                  Patient Details
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      name="identifier"
                      label="Identifier"
                      variant="outlined"
                      value={formData.identifier}
                      onChange={handleFormDataChange}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      name="age"
                      label="Age"
                      type="number"
                      variant="outlined"
                      value={formData.age}
                      onChange={handleFormDataChange}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel shrink={true}>Gender</InputLabel>
                      <Select
                        name="gender"
                        value={formData.gender}
                        onChange={handleFormDataChange}
                        label="Gender"
                        displayEmpty
                        renderValue={value => value || "Select Gender"}
                      >
                        <MenuItem value="male">Male</MenuItem>
                        <MenuItem value="female">Female</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      name="ses"
                      label="Socioeconomic Status (SES)"
                      variant="outlined"
                      value={formData.ses}
                      onChange={handleFormDataChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="occupation"
                      label="Occupation"
                      variant="outlined"
                      value={formData.occupation}
                      onChange={handleFormDataChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="familyHistory"
                      label="Family History"
                      variant="outlined"
                      value={formData.familyHistory}
                      onChange={handleFormDataChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="education"
                      label="Education"
                      variant="outlined"
                      value={formData.education}
                      onChange={handleFormDataChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="mocaScore"
                      label="MoCA Score"
                      type="number"
                      variant="outlined"
                      value={formData.mocaScore}
                      onChange={handleFormDataChange}
                      inputProps={{ min: 0, max: 30 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel shrink={true}>Associated Problems</InputLabel>
                      <Select
                        multiple
                        name="associatedProblems"
                        value={formData.associatedProblems}
                        onChange={handleMultiSelectChange}
                        label="Associated Problems"
                        renderValue={(selected) => selected.join(', ')}
                        displayEmpty
                      >
                        {associatedProblemsOptions.map((problem) => (
                          <MenuItem key={problem} value={problem}>
                            <Checkbox checked={formData.associatedProblems.indexOf(problem) > -1} />
                            {problem}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={savePatientDetails}
                    disabled={saveLoading}
                    sx={{ borderRadius: 2, fontWeight: 'bold' }}
                  >
                    {saveLoading ? <CircularProgress size={24} /> : 'Save Patient Details'}
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Problem Description Accordion */}
            <Accordion
              expanded={expandedSection.problemDescription}
              onChange={handleAccordionChange('problemDescription')}
              sx={{ mb: 3 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
                  Problem Description
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  {problemCategories.map((category) => (
                    <Grid item xs={12} key={category.name}>
                      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2, backgroundColor: '#f8f9fa' }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2e7d32' }}>
                          {category.name}
                        </Typography>

                        <Grid container spacing={2}>
                          {category.subcategories.map((subcategory) => (
                            <Grid item xs={12} sm={6} md={4} key={subcategory}>
                              <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: '500' }}>
                                  {subcategory}
                                </Typography>

                                <RadioGroup
                                  row
                               value={problemDescription[category.name]?.[subcategory]?.response}

                                  onChange={(e) => handleProblemDescriptionChange(category.name, subcategory, e.target.value)}
                                >
                                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                                  <FormControlLabel value="no" control={<Radio />} label="No" />
                                </RadioGroup>

                                {problemDescription[category.name]?.[subcategory]?.response === 'yes' && (
                                  <TextField
                                    fullWidth
                                    variant="outlined"
                                    label="Details"
                                    value={problemDescription[category.name]?.[subcategory]?.details || ''}
                                    onChange={(e) => handleProblemDetailsChange(category.name, subcategory, e.target.value)}
                                    multiline
                                    rows={2}
                                    sx={{ mt: 1 }}
                                  />
                                )}
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={saveProblemDescription}
                    disabled={saveLoading}
                    sx={{ borderRadius: 2, fontWeight: 'bold' }}
                  >
                    {saveLoading ? <CircularProgress size={24} /> : 'Save Problem Description'}
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Health Status Accordion */}
            <Accordion
              expanded={expandedSection.healthStatus}
              onChange={handleAccordionChange('healthStatus')}
              sx={{ mb: 3 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
                  Health Status
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  {healthStatusCategories.map((category) => (
                    <Grid item xs={12} key={category.name}>
                      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2, backgroundColor: '#f8f9fa' }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2e7d32' }}>
                          {category.name}
                        </Typography>

                        <Grid container spacing={2}>
                          {category.options.map((option) => (
                            <Grid item xs={12} sm={6} md={4} key={option}>
                              <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: '500' }}>
                                  {option}
                                </Typography>

                                <RadioGroup
                                  row
                                  value={healthStatus[category.name]?.[option]?.response}
                                  onChange={(e) => handleHealthStatusChange(category.name, option, e.target.value)}
                                >
                                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                                  <FormControlLabel value="no" control={<Radio />} label="No" />
                                </RadioGroup>

                                {healthStatus[category.name]?.[option]?.response === 'yes' && (
                                  <TextField
                                    fullWidth
                                    variant="outlined"
                                    label="Details"
                                    value={healthStatus[category.name]?.[option]?.details || ''}
                                    onChange={(e) => handleHealthDetailsChange(category.name, option, e.target.value)}
                                    multiline
                                    rows={2}
                                    sx={{ mt: 1 }}
                                  />
                                )}
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={saveHealthStatus}
                    disabled={saveLoading}
                    sx={{ borderRadius: 2, fontWeight: 'bold' }}
                  >
                    {saveLoading ? <CircularProgress size={24} /> : 'Save Health Status'}
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Behavioral Assessment Accordion */}
            <Accordion
              expanded={expandedSection.behavioralAssessment}
              onChange={handleAccordionChange('behavioralAssessment')}
              sx={{ mb: 3 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
                  Behavioral Assessment
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  {behavioralCategories.map((category) => (
                    <Grid item xs={12} key={category.name}>
                      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2, backgroundColor: '#f0f7ff' }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2e7d32' }}>
                          {category.name}
                        </Typography>

                        <Grid container spacing={2}>
                          {category.tests.map((test) => (
                            <Grid item xs={12} sm={6} md={4} key={test.key}>
                              <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: '500' }}>
                                  {test.label}
                                </Typography>

                                <RadioGroup
                                  row
                                  value={behavioralAssessment[test.key].response || ''}
                                  onChange={(e) => handleBehavioralResponseChange(test.key, e.target.value)}
                                >
                                  <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                  <FormControlLabel value="No" control={<Radio />} label="No" />
                                </RadioGroup>

                                {behavioralAssessment[test.key].response === 'Yes' && (
                                  <TextField
                                    fullWidth
                                    variant="outlined"
                                    label="Details"
                                    value={behavioralAssessment[test.key].details || ''}
                                    onChange={(e) => handleBehavioralDetailsChange(test.key, e.target.value)}
                                    multiline
                                    rows={2}
                                    sx={{ mt: 1 }}
                                  />
                                )}
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={saveBehavioralAssessment}
                    disabled={saveLoading}
                    sx={{ borderRadius: 2, fontWeight: 'bold' }}
                  >
                    {saveLoading ? <CircularProgress size={24} /> : 'Save Behavioral Assessment'}
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Questionnaire Accordion */}
            <Accordion
              expanded={expandedSection.questionnaire}
              onChange={handleAccordionChange('questionnaire')}
              sx={{ mb: 3 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
                  Questionnaire / Screening Scale
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3} direction="column">
                  {/* Vertigo Symptom Scale */}
                  <Grid item>
                    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2e7d32' }}>
                        Vertigo Symptom Scale
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Max Score"
                            value="60"
                            disabled
                            variant="filled"
                            InputProps={{ style: { fontWeight: 'bold' } }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Patient Score"
                            value={screeningScales.vertigoSymptom.patientScore}
                            onChange={(e) => handleScreeningScaleChange('vertigoSymptom', 'patientScore', e.target.value)}
                            type="number"
                            inputProps={{ min: 0, max: 60 }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Motion Sensitivity Quotient */}
                  <Grid item>
                    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2e7d32' }}>
                        Motion Sensitivity Quotient
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel shrink={true}>Dizziness provoking position</InputLabel>
                            <Select
                              value={screeningScales.motionSensitivity.position}
                              onChange={(e) => handleScreeningScaleChange('motionSensitivity', 'position', e.target.value)}
                              label="Dizziness provoking position"
                              displayEmpty
                              renderValue={value => value || "Select Position"}
                            >
                              {positionOptions.map((option, index) => (
                                <MenuItem key={index} value={option}>{option}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Severity Score"
                            value={screeningScales.motionSensitivity.severity}
                            onChange={(e) => handleScreeningScaleChange('motionSensitivity', 'severity', e.target.value)}
                            type="number"
                            inputProps={{ min: 0, max: 10 }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Dizziness Handicap Inventory */}
                  <Grid item>
                    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2e7d32' }}>
                        Dizziness Handicap Inventory
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            label="Function Score"
                            value={screeningScales.dizzinessHandicap.functionScore}
                            onChange={(e) => handleScreeningScaleChange('dizzinessHandicap', 'functionScore', e.target.value)}
                            type="number"
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            label="Emotional Score"
                            value={screeningScales.dizzinessHandicap.emotionalScore}
                            onChange={(e) => handleScreeningScaleChange('dizzinessHandicap', 'emotionalScore', e.target.value)}
                            type="number"
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            label="Situational Score"
                            value={screeningScales.dizzinessHandicap.situationalScore}
                            onChange={(e) => handleScreeningScaleChange('dizzinessHandicap', 'situationalScore', e.target.value)}
                            type="number"
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            label="Total Score"
                            value={screeningScales.dizzinessHandicap.totalScore}
                            onChange={(e) => handleScreeningScaleChange('dizzinessHandicap', 'totalScore', e.target.value)}
                            type="number"
                            InputProps={{ style: { fontWeight: 'bold' } }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Activities-specific Balance Confidence Scale */}
                  <Grid item>
                    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2e7d32' }}>
                        Activities-specific Balance Confidence Scale
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Least Confidence Activities"
                            value={screeningScales.balanceConfidence.leastConfidence}
                            onChange={(e) => handleScreeningScaleChange('balanceConfidence', 'leastConfidence', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Overall Score"
                            value={screeningScales.balanceConfidence.overallScore}
                            onChange={(e) => handleScreeningScaleChange('balanceConfidence', 'overallScore', e.target.value)}
                            type="number"
                            inputProps={{ min: 0, max: 100 }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* ADDS - Amer's Dizziness Diagnostic Scale */}
                  <Grid item>
                    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2e7d32' }}>
                        ADDS - Amer's Dizziness Diagnostic Scale
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Score"
                            value={screeningScales.addsScale.score}
                            onChange={(e) => handleScreeningScaleChange('addsScale', 'score', e.target.value)}
                            type="number"
                            inputProps={{ min: 0 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Interpretation"
                            value={getAddsInterpretation(screeningScales.addsScale.score)}
                            disabled
                            InputProps={{ style: { fontWeight: 'bold', color: '#c62828' } }}
                          />
                        </Grid>
                      </Grid>
                      <Box sx={{ mt: 2, p: 2, borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                          Interpretation Guide: 0 = Cervicogenic Dizziness, 1-4 = Unilateral Vestibular Hypofunction,
                          5-19 = Positional Vertigo (BPPV), ≥20 = Centrally Mediated Problem
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Modified Clinical Test of Sensory Interaction on Balance (mCTSIB) */}
                  <Grid item>
                    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2e7d32' }}>
                        Modified Clinical Test of Sensory Interaction on Balance (mCTSIB)
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            label="Firm Surface, Eyes Open"
                            value={screeningScales.mctsib.firmEyesOpen}
                            onChange={(e) => handleScreeningScaleChange('mctsib', 'firmEyesOpen', e.target.value)}
                            type="number"
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            label="Firm Surface, Eyes Closed"
                            value={screeningScales.mctsib.firmEyesClosed}
                            onChange={(e) => handleScreeningScaleChange('mctsib', 'firmEyesClosed', e.target.value)}
                            type="number"
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            label="Foam Surface, Eyes Open"
                            value={screeningScales.mctsib.foamEyesOpen}
                            onChange={(e) => handleScreeningScaleChange('mctsib', 'foamEyesOpen', e.target.value)}
                            type="number"
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            label="Foam Surface, Eyes Closed"
                            value={screeningScales.mctsib.foamEyesClosed}
                            onChange={(e) => handleScreeningScaleChange('mctsib', 'foamEyesClosed', e.target.value)}
                            type="number"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Total Score"
                            value={screeningScales.mctsib.totalScore}
                            onChange={(e) => handleScreeningScaleChange('mctsib', 'totalScore', e.target.value)}
                            type="number"
                            InputProps={{ style: { fontWeight: 'bold' } }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={saveQuestionnaire}
                    disabled={saveLoading}
                    sx={{ borderRadius: 2, fontWeight: 'bold' }}
                  >
                    {saveLoading ? <CircularProgress size={24} /> : 'Save Questionnaire'}
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Objective Test Accordion */}
            <Accordion
              expanded={expandedSection.objectivetest}
              onChange={handleAccordionChange('objectivetest')}
              sx={{ mb: 3 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
                  Objective Test
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {/* VEMP Assessment */}
                <Accordion sx={{ mb: 3 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                      VEMP Assessment
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Tabs
                      value={vempType}
                      onChange={handleVempTypeChange}
                      indicatorColor="primary"
                      textColor="primary"
                      sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
                    >
                      <Tab label="c-VEMP" value="cVemp" />
                      <Tab label="o-VEMP" value="oVemp" />
                      <Tab label="m-VEMP" value="mVemp" />
                    </Tabs>

                    {vempType === 'cVemp' && (
                      <Grid container spacing={3}>
                        <Grid item xs={6}>
                          <Paper elevation={2} sx={{ p: 3, backgroundColor: '#e3f2fd' }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#1565c0' }}>
                              Right Ear
                            </Typography>
                            <FormControl component="fieldset" sx={{ mb: 2 }}>
                              <Typography variant="subtitle1">Response</Typography>
                              <RadioGroup
                                row
                                value={vempData.cVemp.right.response}
                                onChange={(e) => handleVempResponseChange('cVemp', 'right', e.target.value)}
                              >
                                <FormControlLabel value="Present" control={<Radio />} label="Present" />
                                <FormControlLabel value="Absent" control={<Radio />} label="Absent" />
                              </RadioGroup>
                            </FormControl>
                            {vempData.cVemp.right.response === 'Present' && (
                              <>
                                <Typography variant="subtitle1" sx={{ mt: 2 }}>P13 Wave</Typography>
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Latency (ms)"
                                      value={vempData.cVemp.right.p13Latency}
                                      onChange={(e) => handleVempFieldChange('cVemp', 'right', 'p13Latency', e.target.value)}
                                      type="number"
                                    />
                                  </Grid>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Amplitude (μV)"
                                      value={vempData.cVemp.right.p13Amplitude}
                                      onChange={(e) => handleVempFieldChange('cVemp', 'right', 'p13Amplitude', e.target.value)}
                                      type="number"
                                    />
                                  </Grid>
                                </Grid>
                                <Typography variant="subtitle1">N23 Wave</Typography>
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Latency (ms)"
                                      value={vempData.cVemp.right.n23Latency}
                                      onChange={(e) => handleVempFieldChange('cVemp', 'right', 'n23Latency', e.target.value)}
                                      type="number"
                                    />
                                  </Grid>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Amplitude (μV)"
                                      value={vempData.cVemp.right.n23Amplitude}
                                      onChange={(e) => handleVempFieldChange('cVemp', 'right', 'n23Amplitude', e.target.value)}
                                      type="number"
                                    />
                                  </Grid>
                                </Grid>
                                <TextField
                                  fullWidth
                                  label="Amplitude Asymmetry Ratio"
                                  value={vempData.cVemp.right.asymmetryRatio}
                                  onChange={(e) => handleVempFieldChange('cVemp', 'right', 'asymmetryRatio', e.target.value)}
                                  sx={{ mt: 2 }}
                                />
                              </>
                            )}
                            <TextField
                              fullWidth
                              label="Interpretation"
                              value={vempData.cVemp.right.interpretation}
                              onChange={(e) => handleVempFieldChange('cVemp', 'right', 'interpretation', e.target.value)}
                              multiline
                              rows={3}
                              sx={{ mt: 3 }}
                            />
                          </Paper>
                        </Grid>
                        <Grid item xs={6}>
                          <Paper elevation={2} sx={{ p: 3, backgroundColor: '#e8f5e9' }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2e7d32' }}>
                              Left Ear
                            </Typography>
                            <FormControl component="fieldset" sx={{ mb: 2 }}>
                              <Typography variant="subtitle1">Response</Typography>
                              <RadioGroup
                                row
                                value={vempData.cVemp.left.response}
                                onChange={(e) => handleVempResponseChange('cVemp', 'left', e.target.value)}
                              >
                                <FormControlLabel value="Present" control={<Radio />} label="Present" />
                                <FormControlLabel value="Absent" control={<Radio />} label="Absent" />
                              </RadioGroup>
                            </FormControl>
                            {vempData.cVemp.left.response === 'Present' && (
                              <>
                                <Typography variant="subtitle1" sx={{ mt: 2 }}>P13 Wave</Typography>
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Latency (ms)"
                                      value={vempData.cVemp.left.p13Latency}
                                      onChange={(e) => handleVempFieldChange('cVemp', 'left', 'p13Latency', e.target.value)}
                                      type="number"
                                    />
                                  </Grid>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Amplitude (μV)"
                                      value={vempData.cVemp.left.p13Amplitude}
                                      onChange={(e) => handleVempFieldChange('cVemp', 'left', 'p13Amplitude', e.target.value)}
                                      type="number"
                                    />
                                  </Grid>
                                </Grid>
                                <Typography variant="subtitle1">N23 Wave</Typography>
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Latency (ms)"
                                      value={vempData.cVemp.left.n23Latency}
                                      onChange={(e) => handleVempFieldChange('cVemp', 'left', 'n23Latency', e.target.value)}
                                      type="number"
                                    />
                                  </Grid>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Amplitude (μV)"
                                      value={vempData.cVemp.left.n23Amplitude}
                                      onChange={(e) => handleVempFieldChange('cVemp', 'left', 'n23Amplitude', e.target.value)}
                                      type="number"
                                    />
                                  </Grid>
                                </Grid>
                                <TextField
                                  fullWidth
                                  label="Amplitude Asymmetry Ratio"
                                  value={vempData.cVemp.left.asymmetryRatio}
                                  onChange={(e) => handleVempFieldChange('cVemp', 'left', 'asymmetryRatio', e.target.value)}
                                  sx={{ mt: 2 }}
                                />
                              </>
                            )}
                            <TextField
                              fullWidth
                              label="Interpretation"
                              value={vempData.cVemp.left.interpretation}
                              onChange={(e) => handleVempFieldChange('cVemp', 'left', 'interpretation', e.target.value)}
                              multiline
                              rows={3}
                              sx={{ mt: 3 }}
                            />
                          </Paper>
                        </Grid>
                      </Grid>
                    )}

                    {vempType === 'oVemp' && (
                      <Grid container spacing={3}>
                        <Grid item xs={6}>
                          <Paper elevation={2} sx={{ p: 3, backgroundColor: '#e3f2fd' }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#1565c0' }}>
                              Right Ear
                            </Typography>
                            <FormControl component="fieldset" sx={{ mb: 2 }}>
                              <Typography variant="subtitle1">Response</Typography>
                              <RadioGroup
                                row
                                value={vempData.oVemp.right.response}
                                onChange={(e) => handleVempResponseChange('oVemp', 'right', e.target.value)}
                              >
                                <FormControlLabel value="Present" control={<Radio />} label="Present" />
                                <FormControlLabel value="Absent" control={<Radio />} label="Absent" />
                              </RadioGroup>
                            </FormControl>
                            {vempData.oVemp.right.response === 'Present' && (
                              <>
                                <Typography variant="subtitle1" sx={{ mt: 2 }}>N1 Wave</Typography>
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Latency (ms)"
                                      value={vempData.oVemp.right.n1Latency}
                                      onChange={(e) => handleVempFieldChange('oVemp', 'right', 'n1Latency', e.target.value)}
                                      type="number"
                                    />
                                  </Grid>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Amplitude (μV)"
                                      value={vempData.oVemp.right.n1Amplitude}
                                      onChange={(e) => handleVempFieldChange('oVemp', 'right', 'n1Amplitude', e.target.value)}
                                      type="number"
                                    />
                                  </Grid>
                                </Grid>
                                <Typography variant="subtitle1">P2 Wave</Typography>
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Latency (ms)"
                                      value={vempData.oVemp.right.p2Latency}
                                      onChange={(e) => handleVempFieldChange('oVemp', 'right', 'p2Latency', e.target.value)}
                                      type="number"
                                    />
                                  </Grid>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Amplitude (μV)"
                                      value={vempData.oVemp.right.p2Amplitude}
                                      onChange={(e) => handleVempFieldChange('oVemp', 'right', 'p2Amplitude', e.target.value)}
                                      type="number"
                                    />
                                  </Grid>
                                </Grid>
                                <TextField
                                  fullWidth
                                  label="Amplitude Asymmetry Ratio"
                                  value={vempData.oVemp.right.asymmetryRatio}
                                  onChange={(e) => handleVempFieldChange('oVemp', 'right', 'asymmetryRatio', e.target.value)}
                                  sx={{ mt: 2 }}
                                />
                              </>
                            )}
                            <TextField
                              fullWidth
                              label="Interpretation"
                              value={vempData.oVemp.right.interpretation}
                              onChange={(e) => handleVempFieldChange('oVemp', 'right', 'interpretation', e.target.value)}
                              multiline
                              rows={3}
                              sx={{ mt: 3 }}
                            />
                          </Paper>
                        </Grid>
                        <Grid item xs={6}>
                          <Paper elevation={2} sx={{ p: 3, backgroundColor: '#e8f5e9' }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2e7d32' }}>
                              Left Ear
                            </Typography>
                            <FormControl component="fieldset" sx={{ mb: 2 }}>
                              <Typography variant="subtitle1">Response</Typography>
                              <RadioGroup
                                row
                                value={vempData.oVemp.left.response}
                                onChange={(e) => handleVempResponseChange('oVemp', 'left', e.target.value)}
                              >
                                <FormControlLabel value="Present" control={<Radio />} label="Present" />
                                <FormControlLabel value="Absent" control={<Radio />} label="Absent" />
                              </RadioGroup>
                            </FormControl>
                            {vempData.oVemp.left.response === 'Present' && (
                              <>
                                <Typography variant="subtitle1" sx={{ mt: 2 }}>N1 Wave</Typography>
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Latency (ms)"
                                      value={vempData.oVemp.left.n1Latency}
                                      onChange={(e) => handleVempFieldChange('oVemp', 'left', 'n1Latency', e.target.value)}
                                      type="number"
                                    />
                                  </Grid>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Amplitude (μV)"
                                      value={vempData.oVemp.left.n1Amplitude}
                                      onChange={(e) => handleVempFieldChange('oVemp', 'left', 'n1Amplitude', e.target.value)}
                                      type="number"
                                    />
                                  </Grid>
                                </Grid>
                                <Typography variant="subtitle1">P2 Wave</Typography>
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Latency (ms)"
                                      value={vempData.oVemp.left.p2Latency}
                                      onChange={(e) => handleVempFieldChange('oVemp', 'left', 'p2Latency', e.target.value)}
                                      type="number"
                                    />
                                  </Grid>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Amplitude (μV)"
                                      value={vempData.oVemp.left.p2Amplitude}
                                      onChange={(e) => handleVempFieldChange('oVemp', 'left', 'p2Amplitude', e.target.value)}
                                      type="number"
                                    />
                                  </Grid>
                                </Grid>
                                <TextField
                                  fullWidth
                                  label="Amplitude Asymmetry Ratio"
                                  value={vempData.oVemp.left.asymmetryRatio}
                                  onChange={(e) => handleVempFieldChange('oVemp', 'left', 'asymmetryRatio', e.target.value)}
                                  sx={{ mt: 2 }}
                                />
                              </>
                            )}
                            <TextField
                              fullWidth
                              label="Interpretation"
                              value={vempData.oVemp.left.interpretation}
                              onChange={(e) => handleVempFieldChange('oVemp', 'left', 'interpretation', e.target.value)}
                              multiline
                              rows={3}
                              sx={{ mt: 3 }}
                            />
                          </Paper>
                        </Grid>
                      </Grid>
                    )}

                    {vempType === 'mVemp' && (
                      <Grid container spacing={3}>
                        <Grid item xs={6}>
                          <Paper elevation={2} sx={{ p: 3, backgroundColor: '#e3f2fd' }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#1565c0' }}>
                              Right Ear
                            </Typography>
                            <FormControl component="fieldset" sx={{ mb: 2 }}>
                              <Typography variant="subtitle1">Response</Typography>
                              <RadioGroup
                                row
                                value={vempData.mVemp.right.response}
                                onChange={(e) => handleVempResponseChange('mVemp', 'right', e.target.value)}
                              >
                                <FormControlLabel value="Present" control={<Radio />} label="Present" />
                                <FormControlLabel value="Absent" control={<Radio />} label="Absent" />
                              </RadioGroup>
                            </FormControl>
                            {vempData.mVemp.right.response === 'Present' && (
                              <>
                                <Typography variant="subtitle1" sx={{ mt: 2 }}>P13 Wave</Typography>
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Latency (ms)"
                                      value={vempData.mVemp.right.p13Latency}
                                      onChange={(e) => handleVempFieldChange('mVemp', 'right', 'p13Latency', e.target.value)}
                                      type="number"
                                    />
                                  </Grid>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Amplitude (μV)"
                                      value={vempData.mVemp.right.p13Amplitude}
                                      onChange={(e) => handleVempFieldChange('mVemp', 'right', 'p13Amplitude', e.target.value)}
                                      type="number"
                                    />
                                  </Grid>
                                </Grid>
                                <Typography variant="subtitle1">N23 Wave</Typography>
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Latency (ms)"
                                      value={vempData.mVemp.right.n23Latency}
                                      onChange={(e) => handleVempFieldChange('mVemp', 'right', 'n23Latency', e.target.value)}
                                      type="number"
                                    />
                                  </Grid>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Amplitude (μV)"
                                      value={vempData.mVemp.right.n23Amplitude}
                                      onChange={(e) => handleVempFieldChange('mVemp', 'right', 'n23Amplitude', e.target.value)}
                                      type="number"
                                    />
                                  </Grid>
                                </Grid>
                                <TextField
                                  fullWidth
                                  label="Amplitude Asymmetry Ratio"
                                  value={vempData.mVemp.right.asymmetryRatio}
                                  onChange={(e) => handleVempFieldChange('mVemp', 'right', 'asymmetryRatio', e.target.value)}
                                  sx={{ mt: 2 }}
                                />
                              </>
                            )}
                            <TextField
                              fullWidth
                              label="Interpretation"
                              value={vempData.mVemp.right.interpretation}
                              onChange={(e) => handleVempFieldChange('mVemp', 'right', 'interpretation', e.target.value)}
                              multiline
                              rows={3}
                              sx={{ mt: 3 }}
                            />
                          </Paper>
                        </Grid>
                        <Grid item xs={6}>
                          <Paper elevation={2} sx={{ p: 3, backgroundColor: '#e8f5e9' }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2e7d32' }}>
                              Left Ear
                            </Typography>
                            <FormControl component="fieldset" sx={{ mb: 2 }}>
                              <Typography variant="subtitle1">Response</Typography>
                              <RadioGroup
                                row
                                value={vempData.mVemp.left.response}
                                onChange={(e) => handleVempResponseChange('mVemp', 'left', e.target.value)}
                              >
                                <FormControlLabel value="Present" control={<Radio />} label="Present" />
                                <FormControlLabel value="Absent" control={<Radio />} label="Absent" />
                              </RadioGroup>
                            </FormControl>
                            {vempData.mVemp.left.response === 'Present' && (
                              <>
                                <Typography variant="subtitle1" sx={{ mt: 2 }}>P13 Wave</Typography>
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Latency (ms)"
                                      value={vempData.mVemp.left.p13Latency}
                                      onChange={(e) => handleVempFieldChange('mVemp', 'left', 'p13Latency', e.target.value)}
                                      type="number"
                                    />
                                  </Grid>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Amplitude (μV)"
                                      value={vempData.mVemp.left.p13Amplitude}
                                      onChange={(e) => handleVempFieldChange('mVemp', 'left', 'p13Amplitude', e.target.value)}
                                      type="number"
                                    />
                                  </Grid>
                                </Grid>
                                <Typography variant="subtitle1">N23 Wave</Typography>
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Latency (ms)"
                                      value={vempData.mVemp.left.n23Latency}
                                      onChange={(e) => handleVempFieldChange('mVemp', 'left', 'n23Latency', e.target.value)}
                                      type="number"
                                    />
                                  </Grid>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Amplitude (μV)"
                                      value={vempData.mVemp.left.n23Amplitude}
                                      onChange={(e) => handleVempFieldChange('mVemp', 'left', 'n23Amplitude', e.target.value)}
                                      type="number"
                                    />
                                  </Grid>
                                </Grid>
                                <TextField
                                  fullWidth
                                  label="Amplitude Asymmetry Ratio"
                                  value={vempData.mVemp.left.asymmetryRatio}
                                  onChange={(e) => handleVempFieldChange('mVemp', 'left', 'asymmetryRatio', e.target.value)}
                                  sx={{ mt: 2 }}
                                />
                              </>
                            )}
                            <TextField
                              fullWidth
                              label="Interpretation"
                              value={vempData.mVemp.left.interpretation}
                              onChange={(e) => handleVempFieldChange('mVemp', 'left', 'interpretation', e.target.value)}
                              multiline
                              rows={3}
                              sx={{ mt: 3 }}
                            />
                          </Paper>
                        </Grid>
                      </Grid>
                    )}
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={saveVempAssessment}
                        disabled={saveLoading}
                        sx={{ borderRadius: 2, fontWeight: 'bold', px: 4, py: 1 }}
                      >
                        {saveLoading ? <CircularProgress size={24} /> : 'Save VEMP Assessment'}
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>

                {/* Head Impulse Paradigm */}
                <Accordion sx={{ mb: 3 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                      Head Impulse Paradigm
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Tabs
                      value={headImpulseType}
                      onChange={(e, newValue) => setHeadImpulseType(newValue)}
                      indicatorColor="primary"
                      textColor="primary"
                      sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
                    >
                      <Tab label="v-HIT" value="vhit" />
                      <Tab label="SHIMP" value="shimp" />
                    </Tabs>

                    <Grid container spacing={3}>
                      {/* Right Horizontal */}
                      <Grid item xs={6}>
                        <Paper elevation={2} sx={{ p: 3, backgroundColor: '#ffebee' }}>
                          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#c62828' }}>
                            Right Horizontal
                          </Typography>
                          <FormControl component="fieldset" sx={{ mb: 2 }}>
                            <Typography variant="subtitle1">Response</Typography>
                            <RadioGroup
                              row
                              value={headImpulseData[headImpulseType].rightHorizontal.response}
                              onChange={(e) => handleHeadImpulseChange('rightHorizontal', 'response', e.target.value)}
                            >
                              <FormControlLabel value="Present" control={<Radio />} label="Present" />
                              <FormControlLabel value="Absent" control={<Radio />} label="Absent" />
                            </RadioGroup>
                          </FormControl>
                          <TextField
                            fullWidth
                            label="VOR Gain"
                            value={headImpulseData[headImpulseType].rightHorizontal.vorGain}
                            onChange={(e) => handleHeadImpulseChange('rightHorizontal', 'vorGain', e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={headImpulseData[headImpulseType].rightHorizontal.response === 'Absent'}
                          />
                          <FormControl component="fieldset" sx={{ mb: 2 }}>
                            <Typography variant="subtitle1">Saccade</Typography>
                            <RadioGroup
                              row
                              value={headImpulseData[headImpulseType].rightHorizontal.saccadePresence}
                              onChange={(e) => handleHeadImpulseChange('rightHorizontal', 'saccadePresence', e.target.value)}
                              disabled={headImpulseData[headImpulseType].rightHorizontal.response === 'Absent'}
                            >
                              <FormControlLabel value="Present" control={<Radio />} label="Present" />
                              <FormControlLabel value="Absent" control={<Radio />} label="Absent" />
                            </RadioGroup>
                          </FormControl>
                          {headImpulseData[headImpulseType].rightHorizontal.saccadePresence === 'Present' && (
                            <FormControl fullWidth sx={{ mb: 2 }}>
                              <InputLabel>Saccade Type</InputLabel>
                              <Select
                                value={headImpulseData[headImpulseType].rightHorizontal.saccadeType}
                                onChange={(e) => handleHeadImpulseChange('rightHorizontal', 'saccadeType', e.target.value)}
                                label="Saccade Type"
                                disabled={headImpulseData[headImpulseType].rightHorizontal.response === 'Absent'}
                              >
                                <MenuItem value="Compensatory">Compensatory</MenuItem>
                                <MenuItem value="Anticompensatory">Anticompensatory</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                          <TextField
                            fullWidth
                            label="Saccade Latency (ms)"
                            value={headImpulseData[headImpulseType].rightHorizontal.saccadeLatency}
                            onChange={(e) => handleHeadImpulseChange('rightHorizontal', 'saccadeLatency', e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={headImpulseData[headImpulseType].rightHorizontal.response === 'Absent'}
                          />
                          <TextField
                            fullWidth
                            label="Interpretation"
                            value={headImpulseData[headImpulseType].rightHorizontal.interpretation}
                            onChange={(e) => handleHeadImpulseChange('rightHorizontal', 'interpretation', e.target.value)}
                            multiline
                            rows={3}
                            sx={{ mt: 2 }}
                          />
                        </Paper>
                      </Grid>

                      {/* Left Horizontal */}
                      <Grid item xs={6}>
                        <Paper elevation={2} sx={{ p: 3, backgroundColor: '#e3f2fd' }}>
                          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#1565c0' }}>
                            Left Horizontal
                          </Typography>
                          <FormControl component="fieldset" sx={{ mb: 2 }}>
                            <Typography variant="subtitle1">Response</Typography>
                            <RadioGroup
                              row
                              value={headImpulseData[headImpulseType].leftHorizontal.response}
                              onChange={(e) => handleHeadImpulseChange('leftHorizontal', 'response', e.target.value)}
                            >
                              <FormControlLabel value="Present" control={<Radio />} label="Present" />
                              <FormControlLabel value="Absent" control={<Radio />} label="Absent" />
                            </RadioGroup>
                          </FormControl>
                          <TextField
                            fullWidth
                            label="VOR Gain"
                            value={headImpulseData[headImpulseType].leftHorizontal.vorGain}
                            onChange={(e) => handleHeadImpulseChange('leftHorizontal', 'vorGain', e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={headImpulseData[headImpulseType].leftHorizontal.response === 'Absent'}
                          />
                          <FormControl component="fieldset" sx={{ mb: 2 }}>
                            <Typography variant="subtitle1">Saccade</Typography>
                            <RadioGroup
                              row
                              value={headImpulseData[headImpulseType].leftHorizontal.saccadePresence}
                              onChange={(e) => handleHeadImpulseChange('leftHorizontal', 'saccadePresence', e.target.value)}
                              disabled={headImpulseData[headImpulseType].leftHorizontal.response === 'Absent'}
                            >
                              <FormControlLabel value="Present" control={<Radio />} label="Present" />
                              <FormControlLabel value="Absent" control={<Radio />} label="Absent" />
                            </RadioGroup>
                          </FormControl>
                          {headImpulseData[headImpulseType].leftHorizontal.saccadePresence === 'Present' && (
                            <FormControl fullWidth sx={{ mb: 2 }}>
                              <InputLabel>Saccade Type</InputLabel>
                              <Select
                                value={headImpulseData[headImpulseType].leftHorizontal.saccadeType}
                                onChange={(e) => handleHeadImpulseChange('leftHorizontal', 'saccadeType', e.target.value)}
                                label="Saccade Type"
                                disabled={headImpulseData[headImpulseType].leftHorizontal.response === 'Absent'}
                              >
                                <MenuItem value="Compensatory">Compensatory</MenuItem>
                                <MenuItem value="Anticompensatory">Anticompensatory</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                          <TextField
                            fullWidth
                            label="Saccade Latency (ms)"
                            value={headImpulseData[headImpulseType].leftHorizontal.saccadeLatency}
                            onChange={(e) => handleHeadImpulseChange('leftHorizontal', 'saccadeLatency', e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={headImpulseData[headImpulseType].leftHorizontal.response === 'Absent'}
                          />
                          <TextField
                            fullWidth
                            label="Interpretation"
                            value={headImpulseData[headImpulseType].leftHorizontal.interpretation}
                            onChange={(e) => handleHeadImpulseChange('leftHorizontal', 'interpretation', e.target.value)}
                            multiline
                            rows={3}
                            sx={{ mt: 2 }}
                          />
                        </Paper>
                      </Grid>
                    </Grid>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={saveHeadImpulse}
                        disabled={saveLoading}
                        sx={{ borderRadius: 2, fontWeight: 'bold', px: 4, py: 1 }}
                      >
                        {saveLoading ? <CircularProgress size={24} /> : 'Save Head Impulse Data'}
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>

                {/* VNG Testing */}
                <Accordion
                  expanded={expandedSection.vngTesting}
                  onChange={handleAccordionChange('vngTesting')}
                  sx={{ mb: 3 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                      VNG Testing
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Tabs
                      value={vngType}
                      onChange={(e, newValue) => setVngType(newValue)}
                      indicatorColor="primary"
                      textColor="primary"
                      variant="fullWidth"
                      sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        mb: 3,
                        '& .MuiTabs-flexContainer': {
                          justifyContent: 'space-around',
                        }
                      }}
                    >
                      <Tab label="Subjective Visual" value="svv" sx={{ fontWeight: 'bold', fontSize: '0.9rem', minWidth: 0, padding: '6px 12px' }} />
                      <Tab label="Gaze Test" value="gaze" sx={{ fontWeight: 'bold', fontSize: '0.9rem', minWidth: 0, padding: '6px 12px' }} />
                      <Tab label="Saccades" value="saccades" sx={{ fontWeight: 'bold', fontSize: '0.9rem', minWidth: 0, padding: '6px 12px' }} />
                      <Tab label="Optokinetic" value="optokinetic" sx={{ fontWeight: 'bold', fontSize: '0.9rem', minWidth: 0, padding: '6px 12px' }} />
                      <Tab label="Smooth Pursuit" value="smoothPursuit" sx={{ fontWeight: 'bold', fontSize: '0.9rem', minWidth: 0, padding: '6px 12px' }} />
                    </Tabs>

                    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '4px', padding: '20px', backgroundColor: '#fff' }}>
                      {vngType === 'svv' && (
                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#1565c0' }}>
                              Subjective Visual Vertical / Horizontal
                            </Typography>
                     
                     <Accordion>
                           <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                             <Typography variant="subtitle1">Right Direction</Typography>
                          </AccordionSummary>
                           <AccordionDetails>
                            {/* Clockwise for Right */}
                             <Accordion>
                               <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                 <Typography variant="subtitle2">1. Clockwise</Typography>
                               </AccordionSummary>
                               <AccordionDetails>
                                 <Grid container spacing={2} sx={{ mb: 2 }}>
                                   <Grid item xs={12}>
                                     <TextField
                                      fullWidth
                                      label="Angle of Deviation (Degrees)"
                                      value={vngData.svv.right.clockwise.angle}
                                      onChange={(e) => handleVngChange('svv', 'right', 'clockwise', 'angle', e.target.value)}
                                    />
                                  </Grid>
                                </Grid>
                                <TextField
                                  fullWidth
                                  label="Interpretation"
                                  value={vngData.svv.right.clockwise.interpretation}
                                  onChange={(e) => handleVngChange('svv', 'right', 'clockwise', 'interpretation', e.target.value)}
                                  multiline
                                  rows={3}
                                />
                              </AccordionDetails>
                            </Accordion>

                            {/* Anticlockwise for Right */}
                            <Accordion>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="subtitle2">2. Anticlockwise</Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                  <Grid item xs={12}>
                                    <TextField
                                      fullWidth
                                      label="Angle of Deviation (Degrees)"
                                      value={vngData.svv.right.anticlockwise.angle}
                                      onChange={(e) => handleVngChange('svv', 'right', 'anticlockwise', 'angle', e.target.value)}
                                    />
                                  </Grid>
                                </Grid>
                                <TextField
                                  fullWidth
                                  label="Interpretation"
                                  value={vngData.svv.right.anticlockwise.interpretation}
                                  onChange={(e) => handleVngChange('svv', 'right', 'anticlockwise', 'interpretation', e.target.value)}
                                  multiline
                                  rows={3}
                                />
                              </AccordionDetails>
                            </Accordion>

                            {/* Blank Background for Right */}
                            <Accordion>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="subtitle2">3. Blank Background</Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                  <Grid item xs={12}>
                                    <TextField
                                      fullWidth
                                      label="Angle of Deviation (Degrees)"
                                      value={vngData.svv.right.blank.angle}
                                      onChange={(e) => handleVngChange('svv', 'right', 'blank', 'angle', e.target.value)}
                                    />
                                  </Grid>
                                </Grid>
                                <TextField
                                  fullWidth
                                  label="Interpretation"
                                  value={vngData.svv.right.blank.interpretation}
                                  onChange={(e) => handleVngChange('svv', 'right', 'blank', 'interpretation', e.target.value)}
                                  multiline
                                  rows={3}
                                />
                              </AccordionDetails>
                            </Accordion>
                          </AccordionDetails>
                        </Accordion>

                        {/* Left Direction Section */}
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle1">Left Direction</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            {/* Clockwise for Left */}
                            <Accordion>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="subtitle2">1. Clockwise</Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                  <Grid item xs={12}>
                                    <TextField
                                      fullWidth
                                      label="Angle of Deviation (Degrees)"
                                      value={vngData.svv.left.clockwise.angle}
                                      onChange={(e) => handleVngChange('svv', 'left', 'clockwise', 'angle', e.target.value)}
                                    />
                                  </Grid>
                                </Grid>
                                <TextField
                                  fullWidth
                                  label="Interpretation"
                                  value={vngData.svv.left.clockwise.interpretation}
                                  onChange={(e) => handleVngChange('svv', 'left', 'clockwise', 'interpretation', e.target.value)}
                                  multiline
                                  rows={3}
                                />
                              </AccordionDetails>
                            </Accordion>

                            {/* Anticlockwise for Left */}
                            <Accordion>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="subtitle2">2. Anticlockwise</Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                  <Grid item xs={12}>
                                    <TextField
                                      fullWidth
                                      label="Angle of Deviation (Degrees)"
                                      value={vngData.svv.left.anticlockwise.angle}
                                      onChange={(e) => handleVngChange('svv', 'left', 'anticlockwise', 'angle', e.target.value)}
                                    />
                                  </Grid>
                                </Grid>
                                <TextField
                                  fullWidth
                                  label="Interpretation"
                                  value={vngData.svv.left.anticlockwise.interpretation}
                                  onChange={(e) => handleVngChange('svv', 'left', 'anticlockwise', 'interpretation', e.target.value)}
                                  multiline
                                  rows={3}
                                />
                              </AccordionDetails>
                            </Accordion>

                            {/* Blank Background for Left */}
                            <Accordion>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="subtitle2">3. Blank Background</Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                  <Grid item xs={12}>
                                    <TextField
                                      fullWidth
                                      label="Angle of Deviation (Degrees)"
                                      value={vngData.svv.left.blank.angle}
                                      onChange={(e) => handleVngChange('svv', 'left', 'blank', 'angle', e.target.value)}
                                    />
                                  </Grid>
                                </Grid>
                                <TextField
                                  fullWidth
                                  label="Interpretation"
                                  value={vngData.svv.left.blank.interpretation}
                                  onChange={(e) => handleVngChange('svv', 'left', 'blank', 'interpretation', e.target.value)}
                                  multiline
                                  rows={3}
                                />
                              </AccordionDetails>
                            </Accordion>
                          </AccordionDetails>
                        </Accordion>
                      </Grid>
                      <Grid item xs={12} sx={{ mt: 3, textAlign: 'center' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={saveSvv}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 'bold',
                            px: 4,
                            py: 1
                          }}
                        >
                          Save SVV Data
                        </Button>
                      </Grid>
                    </Grid>
                  )}



                      {vngType === 'gaze' && (
                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2e7d32' }}>
                              Gaze Test
                            </Typography>
 
                        {['centre', 'right', 'left', 'up', 'down'].map((position) => (
                          <Accordion key={position} sx={{ mb: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>
                                {position}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Grid container spacing={2}>
                                <Grid item xs={12}>
                                  <FormControl fullWidth sx={{ mb: 2 }}>
                                    <InputLabel>Nystagmus</InputLabel>
                                    <Select
                                      value={vngData.gazeTest[position].nystagmus}
                                      onChange={(e) => handleVngChange('gazeTest', position, 'nystagmus', e.target.value)}
                                      label="Nystagmus"
                                    >
                                      <MenuItem value="Present">Present</MenuItem>
                                      <MenuItem value="Absent">Absent</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    label="Interpretation"
                                    value={vngData.gazeTest[position].interpretation}
                                    onChange={(e) => handleVngChange('gazeTest', position, 'interpretation', e.target.value)}
                                    multiline
                                    rows={3}
                                  />
                                </Grid>
                              </Grid>
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </Grid>
                      <Grid item xs={12} sx={{ mt: 3, textAlign: 'center' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={saveGaze}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 'bold',
                            px: 4,
                            py: 1
                          }}
                        >
                          Save Gaze Test Data
                        </Button>
                      </Grid>
                    </Grid>
                  )}

                      {vngType === 'saccades' && (
                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#c62828' }}>
                              Saccades
                            </Typography>
                           
                       {[
                          { key: 'horizontal03', label: '1. Horizontal (0.3 Hz)' },
                          { key: 'horizontal06', label: '2. Horizontal (0.6 Hz)' },
                          { key: 'vertical03', label: '3. Vertical (0.3 Hz)' },
                          { key: 'vertical06', label: '4. Vertical (0.6 Hz)' }
                        ].map((test) => (
                          <Accordion key={test.key} sx={{ mb: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="subtitle1">{test.label}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Grid container spacing={2}>
                                {['latency', 'accuracy', 'velocity'].map((metric) => (
                                  <Grid item xs={4} key={metric}>
                                    <TextField
                                      fullWidth
                                      label={`${metric.charAt(0).toUpperCase() + metric.slice(1)}`}
                                      value={vngData.saccades[test.key][metric]}
                                      onChange={(e) => handleVngChange('saccades', test.key, metric, e.target.value)}
                                    />
                                  </Grid>
                                ))}
                                <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    label="Interpretation"
                                    value={vngData.saccades[test.key].interpretation}
                                    onChange={(e) => handleVngChange('saccades', test.key, 'interpretation', e.target.value)}
                                    multiline
                                    rows={3}
                                  />
                                </Grid>
                              </Grid>
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </Grid>
                      <Grid item xs={12} sx={{ mt: 3, textAlign: 'center' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={saveSaccades}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 'bold',
                            px: 4,
                            py: 1
                          }}
                        >
                          Save Saccades Data
                        </Button>
                      </Grid>
                    </Grid>
                  )}


                      {vngType === 'optokinetic' && (
                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#f57f17' }}>
                              Optokinetic
                            </Typography>
                                            {[
                          { key: 'leftToRight10', label: '1. Left to Right (10)' },
                          { key: 'leftToRight20', label: '2. Left to Right (20)' },
                          { key: 'rightToLeft10', label: '3. Right to Left (10)' },
                          { key: 'rightToLeft20', label: '4. Right to Left (20)' },
                          { key: 'topToBottom10', label: '5. Top to Bottom (10)' },
                          { key: 'topToBottom20', label: '6. Top to Bottom (20)' },
                          { key: 'bottomToTop10', label: '7. Bottom to Top (10)' },
                          { key: 'bottomToTop20', label: '8. Bottom to Top (20)' }
                        ].map((test) => (
                          <Accordion key={test.key} sx={{ mb: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="subtitle1">{test.label}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  <Typography variant="body2">Gain</Typography>
                                  <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                      <TextField
                                        fullWidth
                                        label="Right Eye"
                                        value={vngData.optokinetic[test.key].gainRight}
                                        onChange={(e) => handleVngChange('optokinetic', test.key, 'gainRight', e.target.value)}
                                        size="small"
                                      />
                                    </Grid>
                                    <Grid item xs={6}>
                                      <TextField
                                        fullWidth
                                        label="Left Eye"
                                        value={vngData.optokinetic[test.key].gainLeft}
                                        onChange={(e) => handleVngChange('optokinetic', test.key, 'gainLeft', e.target.value)}
                                        size="small"
                                      />
                                    </Grid>
                                  </Grid>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="body2">Fast Phase Direction</Typography>
                                  <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                      <TextField
                                        fullWidth
                                        label="Right Eye"
                                        value={vngData.optokinetic[test.key].fastPhaseRight}
                                        onChange={(e) => handleVngChange('optokinetic', test.key, 'fastPhaseRight', e.target.value)}
                                        size="small"
                                      />
                                    </Grid>
                                    <Grid item xs={6}>
                                      <TextField
                                        fullWidth
                                        label="Left Eye"
                                        value={vngData.optokinetic[test.key].fastPhaseLeft}
                                        onChange={(e) => handleVngChange('optokinetic', test.key, 'fastPhaseLeft', e.target.value)}
                                        size="small"
                                      />
                                    </Grid>
                                  </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    label="Interpretation"
                                    value={vngData.optokinetic[test.key].interpretation}
                                    onChange={(e) => handleVngChange('optokinetic', test.key, 'interpretation', e.target.value)}
                                    multiline
                                    rows={3}
                                  />
                                </Grid>
                              </Grid>
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </Grid>
                      <Grid item xs={12} sx={{ mt: 3, textAlign: 'center' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={saveOpto}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 'bold',
                            px: 4,
                            py: 1
                          }}
                        >
                          Save Optokinetic Data
                        </Button>
                      </Grid>
                    </Grid>
                  )}

                      {vngType === 'smoothPursuit' && (
                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#f57f17' }}>
                              Smooth Pursuit
                            </Typography>
                        
                         {[
                          { key: 'horizontal02', label: '1. Horizontal (0.2 Hz)' },
                          { key: 'horizontal04', label: '2. Horizontal (0.4 Hz)' },
                          { key: 'vertical02', label: '3. Vertical (0.2 Hz)' },
                          { key: 'vertical06', label: '4. Vertical (0.6 Hz)' }
                        ].map((test) => (
                          <Accordion key={test.key} sx={{ mb: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="subtitle1">{test.label}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  <Typography variant="body2">Rightward Gain</Typography>
                                  <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                      <TextField
                                        fullWidth
                                        label="Right Eye"
                                        value={vngData.smoothPursuit[test.key].rightwardRight}
                                        onChange={(e) => handleVngChange('smoothPursuit', test.key, 'rightwardRight', e.target.value)}
                                        size="small"
                                      />
                                    </Grid>
                                    <Grid item xs={6}>
                                      <TextField
                                        fullWidth
                                        label="Left Eye"
                                        value={vngData.smoothPursuit[test.key].rightwardLeft}
                                        onChange={(e) => handleVngChange('smoothPursuit', test.key, 'rightwardLeft', e.target.value)}
                                        size="small"
                                      />
                                    </Grid>
                                  </Grid>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="body2">Leftward Gain</Typography>
                                  <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                      <TextField
                                        fullWidth
                                        label="Right Eye"
                                        value={vngData.smoothPursuit[test.key].leftwardRight}
                                        onChange={(e) => handleVngChange('smoothPursuit', test.key, 'leftwardRight', e.target.value)}
                                        size="small"
                                      />
                                    </Grid>
                                    <Grid item xs={6}>
                                      <TextField
                                        fullWidth
                                        label="Left Eye"
                                        value={vngData.smoothPursuit[test.key].leftwardLeft}
                                        onChange={(e) => handleVngChange('smoothPursuit', test.key, 'leftwardLeft', e.target.value)}
                                        size="small"
                                      />
                                    </Grid>
                                  </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    label="Interpretation"
                                    value={vngData.smoothPursuit[test.key].interpretation}
                                    onChange={(e) => handleVngChange('smoothPursuit', test.key, 'interpretation', e.target.value)}
                                    multiline
                                    rows={3}
                                  />
                                </Grid>
                              </Grid>
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </Grid>
                      <Grid item xs={12} sx={{ mt: 3, textAlign: 'center' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={saveSmoothPursuit}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 'bold',
                            px: 4,
                            py: 1
                          }}
                        >
                          Save Smooth Pursuit Data
                        </Button>
                      </Grid>
                    </Grid>
                  )}

                    </Box>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={saveVngTesting}
                        disabled={saveLoading}
                        sx={{ borderRadius: 2, fontWeight: 'bold', px: 4, py: 1 }}
                      >
                        {saveLoading ? <CircularProgress size={24} /> : 'Save VNG Testing Data'}
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>

                {/* Positional Tests */}
                <Accordion
                  expanded={expandedSection.positionalTests}
                  onChange={handleAccordionChange('positionalTests')}
                  sx={{ mb: 3 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                      Positional Tests
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Tabs
                      value={positionalTestType}
                      onChange={(e, newValue) => setPositionalTestType(newValue)}
                      indicatorColor="primary"
                      textColor="primary"
                      sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
                    >
                      <Tab label="Dix-Hallpike (Posterior)" value="dixHallpike" />
                      <Tab label="Supine Roll (Horizontal)" value="supineRoll" />
                      <Tab label="Deep Head Hanging (Anterior)" value="deepHeadHanging" />
                    </Tabs>

                    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '4px', padding: '20px', backgroundColor: '#fff' }}>
                      {positionalTestType === 'dixHallpike' && (
                       <Grid container spacing={3}>
                     {/* Right Ear */}
                     <Grid item xs={6}>
                       <Paper elevation={2} sx={{ p: 3, backgroundColor: '#ffebee' }}>
                         <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#c62828' }}>
                           Right Ear
                         </Typography>

                         {/* Nystagmus */}
                         <FormControl component="fieldset" sx={{ mb: 2 }}>
                           <Typography variant="subtitle1">Nystagmus</Typography>
                           <RadioGroup
                            row
                            value={positionalData.dixHallpike.right.nystagmus.present ? 'present' : 'absent'}
                            onChange={(e) => handlePositionalChange('dixHallpike', 'right', 'nystagmus', 'present', e.target.value === 'present')}
                          >
                            <FormControlLabel value="present" control={<Radio />} label="Present" />
                            <FormControlLabel value="absent" control={<Radio />} label="Absent" />
                          </RadioGroup>
                        </FormControl>

                        {/* Nystagmus Type (if present) */}
                        {positionalData.dixHallpike.right.nystagmus.present && (
                          <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Nystagmus Type</InputLabel>
                            <Select
                              value={positionalData.dixHallpike.right.nystagmus.type}
                              onChange={(e) => handlePositionalChange('dixHallpike', 'right', 'nystagmus', 'type', e.target.value)}
                              label="Nystagmus Type"
                            >
                              <MenuItem value="Torsional">Torsional</MenuItem>
                              <MenuItem value="Rotational">Rotational</MenuItem>
                              <MenuItem value="Vertical">Vertical</MenuItem>
                              <MenuItem value="Horizontal">Horizontal</MenuItem>
                            </Select>
                          </FormControl>
                        )}

                        {/* Nystagmus Direction */}
                        <FormControl fullWidth sx={{ mb: 2 }}>
                          <InputLabel>Nystagmus Direction</InputLabel>
                          <Select
                            value={positionalData.dixHallpike.right.nystagmusDirection}
                            onChange={(e) => handlePositionalChange('dixHallpike', 'right', 'nystagmusDirection', '', e.target.value)}
                            label="Nystagmus Direction"
                          >
                            <MenuItem value="Geotropic">Geotropic</MenuItem>
                            <MenuItem value="Ageotropic">Ageotropic</MenuItem>
                          </Select>
                        </FormControl>

                        {/* Change in Direction */}
                        <FormControl component="fieldset" sx={{ mb: 2 }}>
                          <Typography variant="subtitle1">Change in Nystagmus Direction</Typography>
                          <RadioGroup
                            row
                            value={positionalData.dixHallpike.right.changeDirection ? 'yes' : 'no'}
                            onChange={(e) => handlePositionalChange('dixHallpike', 'right', 'changeDirection', '', e.target.value === 'yes')}
                          >
                            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                            <FormControlLabel value="no" control={<Radio />} label="No" />
                          </RadioGroup>
                        </FormControl>

                        {/* Latency */}
                        <TextField
                          fullWidth
                          label="Latency (seconds)"
                          type="number"
                          value={positionalData.dixHallpike.right.latency}
                          onChange={(e) => handlePositionalChange('dixHallpike', 'right', 'latency', '', e.target.value)}
                          sx={{ mb: 2 }}
                        />

                        {/* Vertigo */}
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={6}>
                            <FormControl fullWidth>
                              <InputLabel shrink={true}>Vertigo Intensity</InputLabel>
                              <Select
                                value={positionalData.dixHallpike.right.vertigo.intensity}
                                onChange={(e) => handlePositionalChange('dixHallpike', 'right', 'vertigo', 'intensity', e.target.value)}
                                label="Vertigo Intensity"
                                displayEmpty
                                renderValue={value => value || "Select Intensity"}
                              >
                                <MenuItem value="Mild">Mild</MenuItem>
                                <MenuItem value="Moderate">Moderate</MenuItem>
                                <MenuItem value="Severe">Severe</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Duration (minutes)"
                              type="number"
                              value={positionalData.dixHallpike.right.vertigo.duration}
                              onChange={(e) => handlePositionalChange('dixHallpike', 'right', 'vertigo', 'duration', e.target.value)}
                            />
                          </Grid>
                        </Grid>

                        {/* Recovery */}
                        <TextField
                          fullWidth
                          label="Recovery Phase Duration (minutes)"
                          type="number"
                          value={positionalData.dixHallpike.right.recoveryDuration}
                          onChange={(e) => handlePositionalChange('dixHallpike', 'right', 'recoveryDuration', '', e.target.value)}
                        />
                        <TextField
                          fullWidth
                          label="Interpretation"
                          value={positionalData.dixHallpike.right.interpretation}
                          onChange={(e) => handlePositionalChange('dixHallpike', 'right', 'interpretation', '', e.target.value)}
                          multiline
                          rows={3}
                          sx={{ mt: 3 }}
                        />
                      </Paper>
                    </Grid>

                    {/* Left Ear */}
                    <Grid item xs={6}>
                      <Paper elevation={2} sx={{ p: 3, backgroundColor: '#e3f2fd' }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#1565c0' }}>
                          Left Ear
                        </Typography>

                        {/* Nystagmus */}
                        <FormControl component="fieldset" sx={{ mb: 2 }}>
                          <Typography variant="subtitle1">Nystagmus</Typography>
                          <RadioGroup
                            row
                            value={positionalData.dixHallpike.left.nystagmus.present ? 'present' : 'absent'}
                            onChange={(e) => handlePositionalChange('dixHallpike', 'left', 'nystagmus', 'present', e.target.value === 'present')}
                          >
                            <FormControlLabel value="present" control={<Radio />} label="Present" />
                            <FormControlLabel value="absent" control={<Radio />} label="Absent" />
                          </RadioGroup>
                        </FormControl>

                        {/* Nystagmus Type (if present) */}
                        {positionalData.dixHallpike.left.nystagmus.present && (
                          <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Nystagmus Type</InputLabel>
                            <Select
                              value={positionalData.dixHallpike.left.nystagmus.type}
                              onChange={(e) => handlePositionalChange('dixHallpike', 'left', 'nystagmus', 'type', e.target.value)}
                              label="Nystagmus Type"
                            >
                              <MenuItem value="Torsional">Torsional</MenuItem>
                              <MenuItem value="Rotational">Rotational</MenuItem>
                              <MenuItem value="Vertical">Vertical</MenuItem>
                              <MenuItem value="Horizontal">Horizontal</MenuItem>
                            </Select>
                          </FormControl>
                        )}

                        {/* Nystagmus Direction */}
                        <FormControl fullWidth sx={{ mb: 2 }}>
                          <InputLabel>Nystagmus Direction</InputLabel>
                          <Select
                            value={positionalData.dixHallpike.left.nystagmusDirection}
                            onChange={(e) => handlePositionalChange('dixHallpike', 'left', 'nystagmusDirection', '', e.target.value)}
                            label="Nystagmus Direction"
                          >
                            <MenuItem value="Geotropic">Geotropic</MenuItem>
                            <MenuItem value="Ageotropic">Ageotropic</MenuItem>
                          </Select>
                        </FormControl>

                        {/* Change in Direction */}
                        <FormControl component="fieldset" sx={{ mb: 2 }}>
                          <Typography variant="subtitle1">Change in Nystagmus Direction</Typography>
                          <RadioGroup
                            row
                            value={positionalData.dixHallpike.left.changeDirection ? 'yes' : 'no'}
                            onChange={(e) => handlePositionalChange('dixHallpike', 'left', 'changeDirection', '', e.target.value === 'yes')}
                          >
                            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                            <FormControlLabel value="no" control={<Radio />} label="No" />
                          </RadioGroup>
                        </FormControl>

                        {/* Latency */}
                        <TextField
                          fullWidth
                          label="Latency (seconds)"
                          type="number"
                          value={positionalData.dixHallpike.left.latency}
                          onChange={(e) => handlePositionalChange('dixHallpike', 'left', 'latency', '', e.target.value)}
                          sx={{ mb: 2 }}
                        />

                        {/* Vertigo */}
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={6}>
                            <FormControl fullWidth>
                              <InputLabel shrink={true}>Vertigo Intensity</InputLabel>
                              <Select
                                value={positionalData.dixHallpike.left.vertigo.intensity}
                                onChange={(e) => handlePositionalChange('dixHallpike', 'left', 'vertigo', 'intensity', e.target.value)}
                                label="Vertigo Intensity"
                                displayEmpty
                                renderValue={value => value || "Select Intensity"}
                              >
                                <MenuItem value="Mild">Mild</MenuItem>
                                <MenuItem value="Moderate">Moderate</MenuItem>
                                <MenuItem value="Severe">Severe</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Duration (minutes)"
                              type="number"
                              value={positionalData.dixHallpike.left.vertigo.duration}
                              onChange={(e) => handlePositionalChange('dixHallpike', 'left', 'vertigo', 'duration', e.target.value)}
                            />
                          </Grid>
                        </Grid>

                        {/* Recovery */}
                        <TextField
                          fullWidth
                          label="Recovery Phase Duration (minutes)"
                          type="number"
                          value={positionalData.dixHallpike.left.recoveryDuration}
                          onChange={(e) => handlePositionalChange('dixHallpike', 'left', 'recoveryDuration', '', e.target.value)}
                        />
                        <TextField
                          fullWidth
                          label="Interpretation"
                          value={positionalData.dixHallpike.left.interpretation}
                          onChange={(e) => handlePositionalChange('dixHallpike', 'left', 'interpretation', '', e.target.value)}
                          multiline
                          rows={3}
                          sx={{ mt: 3 }}
                        />
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sx={{ mt: 3, textAlign: 'center' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={saveDixHallpike}
                        sx={{
                          borderRadius: 2,
                          fontWeight: 'bold',
                          px: 4,
                          py: 1
                        }}
                      >
                        Save Dix-Hallpike Data
                      </Button>
                    </Grid>
                  </Grid>
                )}


                      {positionalTestType === 'supineRoll' && (
                        <Grid container spacing={3}>

                     <Grid item xs={6}>
                       <Paper elevation={2} sx={{ p: 3, backgroundColor: '#e3f2fd' }}>
                         <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#c62828' }}>
                           Right Ear 90
                         </Typography>

                         {/* Nystagmus */}
                         <FormControl component="fieldset" sx={{ mb: 2 }}>
                           <Typography variant="subtitle1">Nystagmus</Typography>
                           <RadioGroup
                            row
                            value={positionalData.supineRoll.right.nystagmus.present ? 'present' : 'absent'}
                            onChange={(e) => handlePositionalChange('supineRoll', 'right', 'nystagmus', 'present', e.target.value === 'present')}
                          >
                            <FormControlLabel value="present" control={<Radio />} label="Present" />
                            <FormControlLabel value="absent" control={<Radio />} label="Absent" />
                          </RadioGroup>
                        </FormControl>

                        {/* Nystagmus Type (if present) */}
                        {positionalData.supineRoll.right.nystagmus.present && (
                          <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Nystagmus Type</InputLabel>
                            <Select
                              value={positionalData.supineRoll.right.nystagmus.type}
                              onChange={(e) => handlePositionalChange('supineRoll', 'right', 'nystagmus', 'type', e.target.value)}
                              label="Nystagmus Type"
                            >
                              <MenuItem value="Torsional">Torsional</MenuItem>
                              <MenuItem value="Rotational">Rotational</MenuItem>
                              <MenuItem value="Vertical">Vertical</MenuItem>
                              <MenuItem value="Horizontal">Horizontal</MenuItem>
                            </Select>
                          </FormControl>
                        )}

                        {/* Nystagmus Direction */}
                        <FormControl fullWidth sx={{ mb: 2 }}>
                          <InputLabel>Nystagmus Direction</InputLabel>
                          <Select
                            value={positionalData.supineRoll.right.nystagmusDirection}
                            onChange={(e) => handlePositionalChange('supineRoll', 'right', 'nystagmusDirection', '', e.target.value)}
                            label="Nystagmus Direction"
                          >
                            <MenuItem value="Geotropic">Geotropic</MenuItem>
                            <MenuItem value="Ageotropic">Ageotropic</MenuItem>
                          </Select>
                        </FormControl>

                        {/* Change in Direction */}
                        <FormControl component="fieldset" sx={{ mb: 2 }}>
                          <Typography variant="subtitle1">Change in Nystagmus Direction</Typography>
                          <RadioGroup
                            row
                            value={positionalData.supineRoll.right.changeDirection ? 'yes' : 'no'}
                            onChange={(e) => handlePositionalChange('supineRoll', 'right', 'changeDirection', '', e.target.value === 'yes')}
                          >
                            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                            <FormControlLabel value="no" control={<Radio />} label="No" />
                          </RadioGroup>
                        </FormControl>

                        {/* Latency */}
                        <TextField
                          fullWidth
                          label="Latency (seconds)"
                          type="number"
                          value={positionalData.supineRoll.right.latency}
                          onChange={(e) => handlePositionalChange('supineRoll', 'right', 'latency', '', e.target.value)}
                          sx={{ mb: 2 }}
                        />

                        {/* Vertigo */}
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={6}>
                            <FormControl fullWidth>
                              <InputLabel shrink={true}>Vertigo Intensity</InputLabel>
                              <Select
                                value={positionalData.supineRoll.right.vertigo.intensity}
                                onChange={(e) => handlePositionalChange('supineRoll', 'right', 'vertigo', 'intensity', e.target.value)}
                                label="Vertigo Intensity"
                                displayEmpty
                                renderValue={value => value || "Select Intensity"}
                              >
                                <MenuItem value="Mild">Mild</MenuItem>
                                <MenuItem value="Moderate">Moderate</MenuItem>
                                <MenuItem value="Severe">Severe</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Duration (minutes)"
                              type="number"
                              value={positionalData.supineRoll.right.vertigo.duration}
                              onChange={(e) => handlePositionalChange('supineRoll', 'right', 'vertigo', 'duration', e.target.value)}
                            />
                          </Grid>
                        </Grid>

                        {/* Recovery */}
                        <TextField
                          fullWidth
                          label="Recovery Phase Duration (minutes)"
                          type="number"
                          value={positionalData.supineRoll.right.recoveryDuration}
                          onChange={(e) => handlePositionalChange('supineRoll', 'right', 'recoveryDuration', '', e.target.value)}
                        />
                        <TextField
                          fullWidth
                          label="Interpretation"
                          value={positionalData.supineRoll.right.interpretation}
                          onChange={(e) => handlePositionalChange('supineRoll', 'right', 'interpretation', '', e.target.value)}
                          multiline
                          rows={3}
                          sx={{ mt: 3 }}
                        />
                      </Paper>
                    </Grid>

                    {/* Left Ear */}
                    <Grid item xs={6}>
                      <Paper elevation={2} sx={{ p: 3, backgroundColor: '#e3f2fd' }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#1565c0' }}>
                          Left Ear 90
                        </Typography>

                        {/* Nystagmus */}
                        <FormControl component="fieldset" sx={{ mb: 2 }}>
                          <Typography variant="subtitle1">Nystagmus</Typography>
                          <RadioGroup
                            row
                            value={positionalData.supineRoll.left.nystagmus.present ? 'present' : 'absent'}
                            onChange={(e) => handlePositionalChange('supineRoll', 'left', 'nystagmus', 'present', e.target.value === 'present')}
                          >
                            <FormControlLabel value="present" control={<Radio />} label="Present" />
                            <FormControlLabel value="absent" control={<Radio />} label="Absent" />
                          </RadioGroup>
                        </FormControl>

                        {/* Nystagmus Type (if present) */}
                        {positionalData.supineRoll.left.nystagmus.present && (
                          <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Nystagmus Type</InputLabel>
                            <Select
                              value={positionalData.supineRoll.left.nystagmus.type}
                              onChange={(e) => handlePositionalChange('supineRoll', 'left', 'nystagmus', 'type', e.target.value)}
                              label="Nystagmus Type"
                            >
                              <MenuItem value="Torsional">Torsional</MenuItem>
                              <MenuItem value="Rotational">Rotational</MenuItem>
                              <MenuItem value="Vertical">Vertical</MenuItem>
                              <MenuItem value="Horizontal">Horizontal</MenuItem>
                            </Select>
                          </FormControl>
                        )}

                        {/* Nystagmus Direction */}
                        <FormControl fullWidth sx={{ mb: 2 }}>
                          <InputLabel>Nystagmus Direction</InputLabel>
                          <Select
                            value={positionalData.supineRoll.left.nystagmusDirection}
                            onChange={(e) => handlePositionalChange('supineRoll', 'left', 'nystagmusDirection', '', e.target.value)}
                            label="Nystagmus Direction"
                          >
                            <MenuItem value="Geotropic">Geotropic</MenuItem>
                            <MenuItem value="Ageotropic">Ageotropic</MenuItem>
                          </Select>
                        </FormControl>

                        {/* Change in Direction */}
                        <FormControl component="fieldset" sx={{ mb: 2 }}>
                          <Typography variant="subtitle1">Change in Nystagmus Direction</Typography>
                          <RadioGroup
                            row
                            value={positionalData.supineRoll.left.changeDirection ? 'yes' : 'no'}
                            onChange={(e) => handlePositionalChange('supineRoll', 'left', 'changeDirection', '', e.target.value === 'yes')}
                          >
                            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                            <FormControlLabel value="no" control={<Radio />} label="No" />
                          </RadioGroup>
                        </FormControl>

                        {/* Latency */}
                        <TextField
                          fullWidth
                          label="Latency (seconds)"
                          type="number"
                          value={positionalData.supineRoll.left.latency}
                          onChange={(e) => handlePositionalChange('supineRoll', 'left', 'latency', '', e.target.value)}
                          sx={{ mb: 2 }}
                        />

                        {/* Vertigo */}
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={6}>
                            <FormControl fullWidth>
                              <InputLabel shrink={true}>Vertigo Intensity</InputLabel>
                              <Select
                                value={positionalData.supineRoll.left.vertigo.intensity}
                                onChange={(e) => handlePositionalChange('supineRoll', 'left', 'vertigo', 'intensity', e.target.value)}
                                label="Vertigo Intensity"
                                displayEmpty
                                renderValue={value => value || "Select Intensity"}
                              >
                                <MenuItem value="Mild">Mild</MenuItem>
                                <MenuItem value="Moderate">Moderate</MenuItem>
                                <MenuItem value="Severe">Severe</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Duration (minutes)"
                              type="number"
                              value={positionalData.supineRoll.left.vertigo.duration}
                              onChange={(e) => handlePositionalChange('supineRoll', 'left', 'vertigo', 'duration', e.target.value)}
                            />
                          </Grid>
                        </Grid>

                        {/* Recovery */}
                        <TextField
                          fullWidth
                          label="Recovery Phase Duration (minutes)"
                          type="number"
                          value={positionalData.supineRoll.left.recoveryDuration}
                          onChange={(e) => handlePositionalChange('supineRoll', 'left', 'recoveryDuration', '', e.target.value)}
                        />
                        <TextField
                          fullWidth
                          label="Interpretation"
                          value={positionalData.supineRoll.left.interpretation}
                          onChange={(e) => handlePositionalChange('supineRoll', 'left', 'interpretation', '', e.target.value)}
                          multiline
                          rows={3}
                          sx={{ mt: 3 }}
                        />
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sx={{ mt: 3, textAlign: 'center' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={saveSupineRoll}
                        sx={{
                          borderRadius: 2,
                          fontWeight: 'bold',
                          px: 4,
                          py: 1
                        }}
                      >
                        Save Supine Roll Data
                      </Button>
                    </Grid>
                  </Grid>
                )}


                      {positionalTestType === 'deepHeadHanging' && (
                        <Grid container spacing={3}>
                     {/* Right Ear */}
                     <Grid item xs={6}>
                       <Paper elevation={2} sx={{ p: 3, backgroundColor: '#ffebee' }}>
                         <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#c62828' }}>
                           Right Ear 45
                         </Typography>

                         {/* Nystagmus */}
                         <FormControl component="fieldset" sx={{ mb: 2 }}>
                           <Typography variant="subtitle1">Nystagmus</Typography>
                           <RadioGroup
                            row
                            value={positionalData.deepHeadHanging.right.nystagmus.present ? 'present' : 'absent'}
                            onChange={(e) => handlePositionalChange('deepHeadHanging', 'right', 'nystagmus', 'present', e.target.value === 'present')}
                          >
                            <FormControlLabel value="present" control={<Radio />} label="Present" />
                            <FormControlLabel value="absent" control={<Radio />} label="Absent" />
                          </RadioGroup>
                        </FormControl>

                         {/* Nystagmus Type (if present) */}
                         {positionalData.deepHeadHanging.right.nystagmus.present && (
                          <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Nystagmus Type</InputLabel>
                            <Select
                              value={positionalData.deepHeadHanging.right.nystagmus.type}
                              onChange={(e) => handlePositionalChange('deepHeadHanging', 'right', 'nystagmus', 'type', e.target.value)}
                              label="Nystagmus Type"
                            >
                              <MenuItem value="Torsional">Torsional</MenuItem>
                              <MenuItem value="Rotational">Rotational</MenuItem>
                              <MenuItem value="Vertical">Vertical</MenuItem>
                              <MenuItem value="Horizontal">Horizontal</MenuItem>
                            </Select>
                          </FormControl>
                        )}

                     
                          <InputLabel>Nystagmus Direction</InputLabel>
                          <Select
                            value={positionalData.deepHeadHanging.right.nystagmusDirection}
                            onChange={(e) => handlePositionalChange('deepHeadHanging', 'right', 'nystagmusDirection', '', e.target.value)}
                            label="Nystagmus Direction"
                          >
                            <MenuItem value="Geotropic">Geotropic</MenuItem>
                            <MenuItem value="Ageotropic">Ageotropic</MenuItem>
                          </Select>
                    

                     
                        <FormControl component="fieldset" sx={{ mb: 2 }}>
                          <Typography variant="subtitle1">Change in Nystagmus Direction</Typography>
                          <RadioGroup
                            row
                            value={positionalData.deepHeadHanging.right.changeDirection ? 'yes' : 'no'}
                            onChange={(e) => handlePositionalChange('deepHeadHanging', 'right', 'changeDirection', '', e.target.value === 'yes')}
                          >
                            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                            <FormControlLabel value="no" control={<Radio />} label="No" />
                          </RadioGroup>
                        </FormControl>

                        {/* Latency */}
                        <TextField
                          fullWidth
                          label="Latency (seconds)"
                          type="number"
                          value={positionalData.deepHeadHanging.right.latency}
                          onChange={(e) => handlePositionalChange('deepHeadHanging', 'right', 'latency', '', e.target.value)}
                          sx={{ mb: 2 }}
                        />

                        {/* Vertigo */}
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={6}>
                            <FormControl fullWidth>
                              <InputLabel shrink={true}>Vertigo Intensity</InputLabel>
                              <Select
                                value={positionalData.deepHeadHanging.right.vertigo.intensity}
                                onChange={(e) => handlePositionalChange('deepHeadHanging', 'right', 'vertigo', 'intensity', e.target.value)}
                                label="Vertigo Intensity"
                                displayEmpty
                                renderValue={value => value || "Select Intensity"}
                              >
                                <MenuItem value="Mild">Mild</MenuItem>
                                <MenuItem value="Moderate">Moderate</MenuItem>
                                <MenuItem value="Severe">Severe</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Duration (minutes)"
                              type="number"
                              value={positionalData.deepHeadHanging.right.vertigo.duration}
                              onChange={(e) => handlePositionalChange('deepHeadHanging', 'right', 'vertigo', 'duration', e.target.value)}
                            />
                          </Grid>
                        </Grid>

                        {/* Recovery */}
                        <TextField
                          fullWidth
                          label="Recovery Phase Duration (minutes)"
                          type="number"
                          value={positionalData.deepHeadHanging.right.recoveryDuration}
                          onChange={(e) => handlePositionalChange('deepHeadHanging', 'right', 'recoveryDuration', '', e.target.value)}
                        />
                        <TextField
                          fullWidth
                          label="Interpretation"
                          value={positionalData.deepHeadHanging.right.interpretation}
                          onChange={(e) => handlePositionalChange('deepHeadHanging', 'right', 'interpretation', '', e.target.value)}
                          multiline
                          rows={3}
                          sx={{ mt: 3 }}
                        />
                      </Paper>
                    </Grid>

                    {/* Left Ear */}
                    <Grid item xs={6}>
                      <Paper elevation={2} sx={{ p: 3, backgroundColor: '#e3f2fd' }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#1565c0' }}>
                          Left Ear 45
                        </Typography>

                        {/* Nystagmus */}
                        <FormControl component="fieldset" sx={{ mb: 2 }}>
                          <Typography variant="subtitle1">Nystagmus</Typography>
                          <RadioGroup
                            row
                            value={positionalData.deepHeadHanging.left.nystagmus.present ? 'present' : 'absent'}
                            onChange={(e) => handlePositionalChange('deepHeadHanging', 'left', 'nystagmus', 'present', e.target.value === 'present')}
                          >
                            <FormControlLabel value="present" control={<Radio />} label="Present" />
                            <FormControlLabel value="absent" control={<Radio />} label="Absent" />
                          </RadioGroup>
                        </FormControl>

                        {/* Nystagmus Type (if present) */}
                        {positionalData.deepHeadHanging.left.nystagmus.present && (
                          <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Nystagmus Type</InputLabel>
                            <Select
                              value={positionalData.deepHeadHanging.left.nystagmus.type}
                              onChange={(e) => handlePositionalChange('deepHeadHanging', 'left', 'nystagmus', 'type', e.target.value)}
                              label="Nystagmus Type"
                            >
                              <MenuItem value="Torsional">Torsional</MenuItem>
                              <MenuItem value="Rotational">Rotational</MenuItem>
                              <MenuItem value="Vertical">Vertical</MenuItem>
                              <MenuItem value="Horizontal">Horizontal</MenuItem>
                            </Select>
                          </FormControl>
                        )}

                        {/* Nystagmus Direction */}
                        <FormControl fullWidth sx={{ mb: 2 }}>
                          <InputLabel>Nystagmus Direction</InputLabel>
                          <Select
                            value={positionalData.deepHeadHanging.left.nystagmusDirection}
                            onChange={(e) => handlePositionalChange('deepHeadHanging', 'left', 'nystagmusDirection', '', e.target.value)}
                            label="Nystagmus Direction"
                          >
                            <MenuItem value="Geotropic">Geotropic</MenuItem>
                            <MenuItem value="Ageotropic">Ageotropic</MenuItem>
                          </Select>
                        </FormControl>

                        {/* Change in Direction */}
                        <FormControl component="fieldset" sx={{ mb: 2 }}>
                          <Typography variant="subtitle1">Change in Nystagmus Direction</Typography>
                          <RadioGroup
                            row
                            value={positionalData.deepHeadHanging.left.changeDirection ? 'yes' : 'no'}
                            onChange={(e) => handlePositionalChange('deepHeadHanging', 'left', 'changeDirection', '', e.target.value === 'yes')}
                          >
                            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                            <FormControlLabel value="no" control={<Radio />} label="No" />
                          </RadioGroup>
                        </FormControl>

                        {/* Latency */}
                        <TextField
                          fullWidth
                          label="Latency (seconds)"
                          type="number"
                          value={positionalData.deepHeadHanging.left.latency}
                          onChange={(e) => handlePositionalChange('deepHeadHanging', 'left', 'latency', '', e.target.value)}
                          sx={{ mb: 2 }}
                        />

                        {/* Vertigo */}
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={6}>
                            <FormControl fullWidth>
                              <InputLabel shrink={true}>Vertigo Intensity</InputLabel>
                              <Select
                                value={positionalData.deepHeadHanging.left.vertigo.intensity}
                                onChange={(e) => handlePositionalChange('deepHeadHanging', 'left', 'vertigo', 'intensity', e.target.value)}
                                label="Vertigo Intensity"
                                displayEmpty
                                renderValue={value => value || "Select Intensity"}
                              >
                                <MenuItem value="Mild">Mild</MenuItem>
                                <MenuItem value="Moderate">Moderate</MenuItem>
                                <MenuItem value="Severe">Severe</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Duration (minutes)"
                              type="number"
                              value={positionalData.deepHeadHanging.left.vertigo.duration}
                              onChange={(e) => handlePositionalChange('deepHeadHanging', 'left', 'vertigo', 'duration', e.target.value)}
                            />
                          </Grid>
                        </Grid>

                        {/* Recovery */}
                        <TextField
                          fullWidth
                          label="Recovery Phase Duration (minutes)"
                          type="number"
                          value={positionalData.deepHeadHanging.left.recoveryDuration}
                          onChange={(e) => handlePositionalChange('deepHeadHanging', 'left', 'recoveryDuration', '', e.target.value)}
                        />
                        <TextField
                          fullWidth
                          label="Interpretation"
                          value={positionalData.deepHeadHanging.left.interpretation}
                          onChange={(e) => handlePositionalChange('deepHeadHanging', 'left', 'interpretation', '', e.target.value)}
                          multiline
                          rows={3}
                          sx={{ mt: 3 }}
                        />
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sx={{ mt: 3, textAlign: 'center' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={savePositionalTests}
                        sx={{
                          borderRadius: 2,
                          fontWeight: 'bold',
                          px: 4,
                          py: 1
                        }}
                      >
                        Save Deep Head Hanging Data
                      </Button>
                    </Grid>
                  </Grid>
                )}


                    </Box>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={savePositionalTests}
                        disabled={saveLoading}
                        sx={{ borderRadius: 2, fontWeight: 'bold', px: 4, py: 1 }}
                      >
                        {saveLoading ? <CircularProgress size={24} /> : 'Save Positional Tests Data'}
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </AccordionDetails>
            </Accordion>
   <Dialog 
        open={openPasswordModal} 
        onClose={handlePasswordModalClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Download Data
          <IconButton
            aria-label="close"
            onClick={handlePasswordModalClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              variant="outlined"
              sx={{ mb: 3 }}
            />
            <FormControl fullWidth>
              <InputLabel>Format</InputLabel>
              <Select
                value={downloadFormat}
                onChange={(e) => setDownloadFormat(e.target.value)}
                label="Format"
              >
                <MenuItem value="excel">Excel</MenuItem>
                <MenuItem value="pdf">PDF</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handlePasswordModalClose} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleDownloadConfirm} 
            variant="contained" 
            color="primary"
            disabled={!password}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
            {/* Submit Button */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleSubmit}
                disabled={loading}
                sx={{
                  px: 8,
                  py: 1.8,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  borderRadius: 10,
                  boxShadow: 3,
                  background: 'linear-gradient(45deg, #1976d2 30%, #0d47a1 90%)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4
                  }
                }}
              >
                {loading ? <CircularProgress size={24} /> : 'Submit Assessment'}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default VestibularForm;